import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useNavigate, Link as Link$1, Outlet, useParams, Routes, Route, Navigate } from "react-router";
import { ReactiveCore, replaceRows, DexieDatabaseProvider, useSuccessReactor, useMonitor } from "@livefree/reactive";
import { MqlRequestBuilder, RequestMetadata, dexieRequestBrokerAddon, RequestBrokerTable, RequestBroker, Sorting, useRequestBroker, Paging } from "@livefree/request-broker";
import { EnvironmentConfigurations } from "@livefree/runtime";
import Papa from "papaparse";
import { camelCase, formatCurrencyUSD, parseAndFormatDate, DateTime, parseAndFormatDateTime, isEmpty, toISO, deepEquals } from "@livefree/common";
import { App, RequirePolicy, useRecentView, createEmbed } from "@livefree/applications";
import { useCallback, useState, useRef, createElement, useEffect, useId, useMemo } from "react";
import { Chip, Table, Alert, Modal, Button, Skeleton, Link, Tabs, Card, ListBox, Popover, Breadcrumbs, BreadcrumbsItem, Accordion, Tooltip, Spinner, Checkbox, ScrollShadow } from "@heroui/react";
import { CopyToClipboard, ConfirmButton, ResourceActions, ConfirmableButton, withModalController, HelpIcon, Grid, ErrorListChip, KeyDisplay, withDrawerController, NewButton, DeleteButton, Forbidden } from "@livefree/react-ui";
import { Filters, FilterInput, FilterToggle, FilterRange, FilterDateRange, FilterSelect, FilterPresets, FilterConstant } from "@livefree/filters";
import { Uniformly, FieldStatus, DateTimeField, TextField, SelectField, AutocompleteField, PasswordField, AttachmentField, SwitchField, PhoneField, AddressLookupField, useForms } from "@livefree/uniformly";
import { RiCheckboxCircleFill, RiHospitalLine, RiMoneyDollarCircleLine, RiBillLine, RiCashLine, RiBankLine, RiBankCardLine, RiTempColdFill } from "@remixicon/react";
import { WithRouteParams } from "@livefree/react";
const reactive = new ReactiveCore("transactions");
const TransactionsConfig = EnvironmentConfigurations.hydrate({
  development: {
    host: "https://localhost:9002",
    api: {
      host: "https://localhost:7104",
      apiKey: "TransactionsAPI"
    }
  },
  staging: {
    host: "https://lf-artemis-staging.azurewebsites.net",
    api: {
      host: "https://lf-artemis-api-staging.azurewebsites.net",
      apiKey: "TransactionsAPI"
    }
  },
  production: {
    host: [
      "https://artemis.lifebeacon.com",
      "https://app.alert1.com",
      "https://app.lifebeacon.com"
    ],
    api: {
      host: "https://artemis.lfapi.net",
      apiKey: "TransactionsAPI"
    }
  }
});
const Mql = {
  build: (model, metadata) => {
    var result = new MqlRequestBuilder(metadata).buildRequest(`FETCH ${model}`);
    if (result.includes("null")) {
      result = result.replace("null", "");
    }
    return result;
  }
};
const pendingFirst = (mql) => {
  if (mql.includes("SORTBY ")) return mql.replace("SORTBY ", "SORTBY PendingApproval DESC THENBY ");
  const sortBy = " SORTBY PendingApproval DESC ";
  return mql.includes(" SKIP ") ? mql.replace(" SKIP ", `${sortBy}SKIP `) : mql.replace(/;\s*$/, `${sortBy};`);
};
class BanksClient extends ArtemisSDK.BanksClient {
  // Just the tally for the banner. TAKE 1 because WITH COUNT reports the size of the whole
  // match, so a single row is enough to carry it back and there is no reason to ship a page
  // of banks nothing renders.
  queryPendingBanks() {
    return super.queryBanks("FETCH BankModel MATCH[PendingApproval == true] SKIP 0 TAKE 1 WITH COUNT;");
  }
  queryBanks(metadata) {
    return super.queryBanks(
      pendingFirst(metadata ? Mql.build("BankModel", metadata) : "FETCH BankModel;")
    );
  }
}
const BanksService = reactive.service("banks", BanksClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(TransactionsConfig));
BanksService.reactor("banksReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case BanksService.queryBanks:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.banks, payload.values);
      // queryPendingBanks intentionally has no case: the banner reads totalCount off the
      // response, so there is nothing worth putting in Dexie.
      case BanksService.getBank:
        return db.banks.put(payload);
      case BanksService.createBank:
        appRuntime.services.alerts.success(`Bank added successfully`);
        return db.banks.add(payload);
      case BanksService.updateBank:
        appRuntime.services.alerts.success(`Bank updated successfully`);
        return db.banks.put(payload);
      case BanksService.deleteBank:
        appRuntime.services.alerts.success(`Bank deleted successfully`);
        return db.banks.delete(args[0]);
    }
  },
  error: (response, services) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const FundingTypesService = reactive.service(
  "fundingTypes",
  ArtemisSDK.FundingTypesClient
)((Service) => new Service(TransactionsConfig));
FundingTypesService.reactor("fundingTypesReactor", {
  success: (response, _, db) => {
    const { source, payload } = response;
    switch (source) {
      case FundingTypesService.getFundingTypes:
        return db.fundingTypes.bulkPut(payload);
    }
  },
  error: (response, services) => {
    return appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class MerchantsClient extends ArtemisSDK.MerchantsClient {
  queryMerchants(metadata) {
    return metadata ? super.queryMerchants(Mql.build("MerchantModel", metadata)) : super.queryMerchants("FETCH MerchantModel;");
  }
}
const MerchantsService = reactive.service("merchants", MerchantsClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(TransactionsConfig));
MerchantsService.reactor("merchantsReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case MerchantsService.queryMerchants:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.merchants, payload.values);
      case MerchantsService.getMerchant:
        return db.merchants.put(payload);
      case MerchantsService.createMerchant:
        appRuntime.services.alerts.success(`Merchant added successfully`);
        return db.merchants.add(payload);
      case MerchantsService.updateMerchant:
        appRuntime.services.alerts.success(`Merchant updated successfully`);
        return db.merchants.put(payload);
      case MerchantsService.deleteMerchant:
        appRuntime.services.alerts.success(`Merchant deleted successfully`);
        return db.merchants.delete(args[0]);
    }
  },
  error: (response, services) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class MerchantTypesClient extends ArtemisSDK.MerchantTypesClient {
  queryMerchantTypes(metadata) {
    return metadata ? super.queryMerchantTypes(Mql.build("MerchantTypeModel", metadata)) : super.queryMerchantTypes("FETCH MerchantTypeModel;");
  }
}
const MerchantTypesService = reactive.service("merchantTypes", MerchantTypesClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(TransactionsConfig));
MerchantTypesService.reactor("merchantTypesReactor", {
  success: (response, services, db) => {
    const { source, payload } = response;
    switch (source) {
      case MerchantTypesService.queryMerchantTypes:
        return replaceRows(db, db.merchantTypes, payload.values);
      default:
        appRuntime.services.alerts.success("Success");
        return db;
    }
  },
  error: (response, services) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class ProfilesClient extends ArtemisSDK.ProfilesClient {
  queryProfiles(metadata) {
    return super.queryProfiles(Mql.build("ProfileModel", metadata));
  }
}
const ProfilesService = reactive.service("profiles", ProfilesClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(TransactionsConfig));
ProfilesService.reactor("profilesReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case ProfilesService.queryProfiles:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.profiles, payload.values);
      case ProfilesService.getProfiles:
        return db.profiles.bulkPut(payload);
      case ProfilesService.profileOperation: {
        const operation = args[0];
        const message = operation === "unfreeze-profile" ? "Profile unfreezed successfully" : "Profile flagged successfully";
        appRuntime.services.alerts.success(message);
        return db.profiles.put(payload);
      }
      case ProfilesService.getProfile:
        return db.profiles.put(payload);
      case ProfilesService.createProfile:
        appRuntime.services.alerts.success(`Profile added successfully`);
        return db.profiles.add(payload);
      case ProfilesService.updateProfile:
        appRuntime.services.alerts.success(`Profile updated successfully`);
        return db.profiles.put(payload);
      case ProfilesService.deleteProfile:
        appRuntime.services.alerts.success(`Profile deleted successfully`);
        return db.profiles.delete(args[0]);
    }
  },
  error: (response, services) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class ResponseCodesClient extends ArtemisSDK.ResponseCodesClient {
  queryResponseCodes(metadata) {
    return metadata ? super.queryResponseCodes(Mql.build("ResponseCodeModel", metadata)) : super.queryResponseCodes("FETCH ResponseCodeModel;");
  }
  queryResponseCodesTotals() {
    return super.queryResponseCodes("FETCH ResponseCodeModel;");
  }
}
const ResponseCodesService = reactive.service("responseCodes", ResponseCodesClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(TransactionsConfig));
ResponseCodesService.reactor("responseCodesReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case ResponseCodesService.queryResponseCodes:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.responseCodes, payload.values);
      case ResponseCodesService.queryResponseCodesTotals:
        return db.responseCodesTotals.add(payload);
      case ResponseCodesService.createResponseCode:
        appRuntime.services.alerts.success(`ResponseCode added successfully`);
        return db.responseCodes.add(payload);
      case ResponseCodesService.updateResponseCode:
        appRuntime.services.alerts.success(`ResponseCode updated successfully`);
        return db.responseCodes.put(payload);
      case ResponseCodesService.deleteResponseCode:
        appRuntime.services.alerts.success(`ResponseCode deleted successfully`);
        return db.responseCodes.delete(args[0]);
    }
  },
  error: (response, services) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
ResponseCodesService.queryResponseCodesTotals.interceptor("post", (payload) => {
  const totalsByType = payload.values.reduce((acc, { responseCodeType }) => {
    acc[responseCodeType] = (acc[responseCodeType] || 0) + 1;
    return acc;
  }, {});
  return totalsByType;
});
const ResponseCodeTypesService = reactive.service(
  "responseCodeTypes",
  ArtemisSDK.ResponseCodeTypesClient
)((Service) => new Service(TransactionsConfig));
ResponseCodeTypesService.reactor("responseCodeTypesReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case ResponseCodeTypesService.queryResponseCodeTypes:
        return db.responseCodeTypes.bulkPut(payload);
      case ResponseCodeTypesService.getResponseCodeType:
        return db.responseCodeTypes.put(payload);
      case ResponseCodeTypesService.createResponseCodeType:
        appRuntime.services.alerts.success(`ResponseCodeType added successfully`);
        return db.responseCodeTypes.add(payload);
      case ResponseCodeTypesService.updateResponseCodeType:
        appRuntime.services.alerts.success(`ResponseCodeType updated successfully`);
        return db.responseCodeTypes.put(payload);
      case ResponseCodeTypesService.deleteResponseCodeType:
        appRuntime.services.alerts.success(`ResponseCodeType deleted successfully`);
        return db.responseCodeTypes.delete(args[0]);
    }
  },
  error: (response, services) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class TransactionBatchesClient extends ArtemisSDK.TransactionBatchesClient {
  queryTransactionBatches(metadata) {
    return metadata ? super.queryTransactionBatches(Mql.build("TransactionBatchModel", metadata)) : super.queryTransactionBatches("FETCH TransactionBatchModel;");
  }
}
const TransactionBatchesService = reactive.service("batches", TransactionBatchesClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(TransactionsConfig));
TransactionBatchesService.reactor("batchesReactor", {
  success: (response, services, db) => {
    const { source, payload, args } = response;
    switch (source) {
      case TransactionBatchesService.queryTransactionBatches:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.batches, payload.values);
      case TransactionBatchesService.updateTransactionBatch:
        appRuntime.services.alerts.success("Batch updated");
        return db.batches.put(payload);
      case TransactionBatchesService.rPC:
        switch (args[1]) {
          case "Reconcile":
            appRuntime.services.alerts.success("Batch reconciled");
            return db.batches.put(payload);
          default:
            appRuntime.services.alerts.success("Procedure completed");
            return db;
        }
      default:
        appRuntime.services.alerts.success("Success");
    }
  },
  error: (response, services) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class TransactionsClient extends ArtemisSDK.TransactionsClient {
  queryTransactions(metadata, batchId = 0) {
    return metadata ? super.queryTransactions(batchId, Mql.build("TransactionModel", metadata)) : super.queryTransactions(batchId, "FETCH TransactionModel;");
  }
}
const TransactionsService = reactive.service("transactions", TransactionsClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(TransactionsConfig));
TransactionsService.reactor("transactionsReactor", {
  success: (response, services, db) => {
    const { source, payload } = response;
    switch (source) {
      case TransactionsService.queryTransactions:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.transactions, payload.values);
      case TransactionsService.updateTransaction:
        appRuntime.services.alerts.success("Transaction updated");
        return db.transactions.put(payload);
      case TransactionsService.getTransaction:
        return db.payments.put(payload);
      case TransactionsService.getChildTransactions:
        return db.payments.bulkPut(payload);
      case TransactionsService.republishTransactionStatusUpdated:
        appRuntime.services.alerts.success("Update event sent to subscribers");
        return db.payments.put(payload);
      default:
        appRuntime.services.alerts.success("Success");
        return db;
    }
  },
  error: (response, services) => {
    if (response.source?.action === "getTransaction") return;
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class PaymentImportsClient extends ArtemisSDK.PaymentImportClient {
  queryPaymentImports(metadata) {
    return metadata ? super.queryPaymentImports(MqlRequestBuilder.create(metadata).build("PaymentImportModel")) : super.queryPaymentImports("FETCH PaymentImportModel;");
  }
  parseCSVDataToJSON(csvData) {
    return new Promise((resolve, reject) => {
      Papa.parse(csvData, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => {
          const normalizedData = results.data.map((row) => {
            const newRow = {};
            Object.keys(row).forEach((key) => {
              const value = row[key];
              if (value !== null && value !== void 0 && value !== "") {
                const camelKey = camelCase(key.replace(/\s/g, ""));
                if (camelKey === "transactionId" || camelKey === "bankAccountNumber") {
                  newRow[camelKey] = String(value);
                } else {
                  newRow[camelKey] = value;
                }
              }
            });
            return newRow;
          });
          resolve(normalizedData);
        },
        error: (error) => {
          reject(new Error("Error parsing CSV file.", error));
        }
      });
    });
  }
}
const PaymentImportsService = reactive.service("paymentImports", PaymentImportsClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(TransactionsConfig));
PaymentImportsService.reactor("paymentImportsReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case PaymentImportsService.getPaymentImport:
        return db.payment_imports.put(payload);
      case PaymentImportsService.queryPaymentImports:
        RequestMetadata.setCount(source.action, payload.totalCount);
        return replaceRows(db, db.payment_imports, payload.values);
      case PaymentImportsService.createPaymentImport:
        appRuntime.services.alerts.success("Payment import created");
        break;
      case PaymentImportsService.parseCSVDataToJSON:
        const [operation, _] = args;
        if (operation === "validate-import") {
          appRuntime.services.alerts.success("Payment import validated");
        }
        break;
      case PaymentImportsService.paymentImportOperation:
        appRuntime.services.alerts.success("Payment import operation completed");
        break;
      default:
        appRuntime.services.alerts.error(`Unhandled action: ${source.action}`);
        break;
    }
  },
  error: (response, services) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class PaymentImportDetailsClient extends ArtemisSDK.PaymentImportDetailsClient {
  queryPaymentImportDetails(metadata, id) {
    return metadata ? super.queryPaymentImportDetails(MqlRequestBuilder.create(metadata).build("PaymentImportDetailsModel")) : super.queryPaymentImportDetails("FETCH PaymentImportDetailsModel;");
  }
}
const PaymentImportDetailsService = reactive.service("paymentImportDetails", PaymentImportDetailsClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(TransactionsConfig));
PaymentImportDetailsService.reactor("paymentImportDetailsReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case PaymentImportDetailsService.queryPaymentImportDetails:
        RequestMetadata.setCount(`${source.action}-${args[1]}`, payload.totalCount);
        return replaceRows(db, db.payment_import_details, payload.values);
      default:
        appRuntime.services.alerts.error(`Unhandled action: ${source.action}`);
        break;
    }
  },
  error: (response, services) => {
    appRuntime.services.alerts.error(response.payload);
  }
});
const storeFormat = (store) => Object.entries(store).reduce((result, [key, value]) => {
  result[key] = value.join(",");
  return result;
}, {});
const ReactiveConfig = {
  db: {
    name: "livefree.transactions.db",
    provider: new DexieDatabaseProvider([dexieRequestBrokerAddon]),
    init: (db) => db.version(8).stores(storeFormat(_store))
  }
};
const _store = {
  banks: ["&routingNumber"],
  batches: ["&id"],
  fundingTypes: ["&id"],
  lookups: ["[type+id]", "value"],
  responseCodeTypes: ["&id"],
  responseCodes: ["&id"],
  merchants: ["&id"],
  payments: ["&id", "parentTransactionId"],
  transactions: ["&id", "batchId"],
  profiles: ["&id"],
  merchantTypes: ["&id"],
  payment_imports: ["&id"],
  payment_import_details: ["&id", "paymentImportId"]
};
const render$2 = reactive.configure(ReactiveConfig).render;
const Shell = ({ children }) => /* @__PURE__ */ jsx("div", { className: "p-4 h-full flex flex-col", children });
let Bootstrapped;
const Layout = ({ children }) => {
  Bootstrapped ??= App.bootstrap(Shell, { runtime: appRuntime });
  return /* @__PURE__ */ jsx(Bootstrapped, { children });
};
const BaseLayout = reactive.configure(ReactiveConfig).render((props) => /* @__PURE__ */ jsx(Layout, { ...props }));
const columns$6 = [
  { propertyName: "routingNumber", name: "Routing Number", isSortable: true },
  { propertyName: "bankName", name: "Bank Name", isSortable: true },
  { propertyName: "telegraphicName", name: "Telegraphic Name", isSortable: true },
  { propertyName: "city", name: "City", isSortable: true },
  { propertyName: "state", name: "State", isSortable: true },
  // Not sortable: the query always puts pending banks first, so a sort here would do nothing.
  { propertyName: "pendingApproval", name: "Status" }
];
const BanksList = ({ banks = [], onSelect }) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "routingNumber":
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
          /* @__PURE__ */ jsx("span", { className: "font-semibold me-2", children: cellValue }),
          /* @__PURE__ */ jsx(CopyToClipboard, { text: cellValue })
        ] });
      case "pendingApproval":
        return /* @__PURE__ */ jsx(Chip, { size: "sm", color: cellValue ? "warning" : "success", variant: "flat", children: cellValue ? "Pending Approval" : "Approved" });
      default:
        return cellValue;
    }
  }, []);
  return /* @__PURE__ */ jsx(
    RequestBrokerTable,
    {
      columns: columns$6,
      items: banks,
      renderColumn: (column) => /* @__PURE__ */ jsx(
        Table.Column,
        {
          isRowHeader: true,
          id: column.propertyName,
          align: "start",
          allowsSorting: column?.isSortable,
          children: column.name
        },
        column.propertyName
      ),
      renderRow: (row) => /* @__PURE__ */ jsx(
        Table.Row,
        {
          id: row.routingNumber,
          onAction: () => onSelect(row),
          className: "cursor-pointer",
          children: columns$6.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(row, column.propertyName) }, column.propertyName))
        },
        row.routingNumber
      )
    }
  );
};
const BankFilterDefinitions = new Filters([
  new FilterInput({
    name: "RoutingNumber",
    title: "Routing Number"
  }),
  new FilterInput({
    name: "BankName",
    title: "Bank Name"
  }),
  new FilterInput({
    name: "TelegraphicName",
    title: "Telegraphic Name"
  }),
  new FilterInput({
    name: "City",
    title: "City"
  }),
  new FilterInput({
    name: "State",
    title: "State"
  }),
  new FilterToggle({
    name: "PendingApproval",
    title: "Pending Approval",
    defaultValue: true
  })
]);
const PendingBanksBanner = reactive.component(
  ({ services }) => {
    const [count, setCount] = useState(0);
    const refresh = useCallback(() => services.banks.queryPendingBanks(), [services]);
    useSuccessReactor(services.banks.queryPendingBanks, (payload) => setCount(payload?.totalCount ?? 0));
    useSuccessReactor(services.banks.createBank, refresh);
    useSuccessReactor(services.banks.updateBank, refresh);
    useSuccessReactor(services.banks.deleteBank, refresh);
    if (!count) return null;
    return /* @__PURE__ */ jsxs(Alert, { status: "warning", className: "mb-4", children: [
      /* @__PURE__ */ jsx(Alert.Indicator, {}),
      /* @__PURE__ */ jsxs(Alert.Content, { children: [
        /* @__PURE__ */ jsx(Alert.Title, { children: count === 1 ? "1 bank is waiting for approval" : `${count} banks are waiting for approval` }),
        /* @__PURE__ */ jsx(Alert.Description, { children: "Added automatically from a routing number the directory did not recognize. They are listed first below." })
      ] })
    ] });
  },
  {
    name: "PendingBanksBanner",
    sync: (services) => [{ execute: () => services.banks.queryPendingBanks(), deps: [] }]
  }
);
const BanksListController = reactive.component(
  ({ onSelect }) => {
    return /* @__PURE__ */ jsx(BanksListLoader, { children: (handleExecute, banks, monitors, services) => {
      return /* @__PURE__ */ jsxs(
        RequestBroker,
        {
          actionKey: services.banks.queryBanks.action,
          action: services.banks.queryBanks,
          filters: BankFilterDefinitions,
          isExecuting: monitors.queryBanks.executing,
          onExecute: handleExecute,
          children: [
            /* @__PURE__ */ jsx(PendingBanksBanner, {}),
            /* @__PURE__ */ jsx(
              BanksList,
              {
                banks,
                isLoading: monitors.queryBanks.executing,
                onSelect: (item) => onSelect && onSelect(item)
              }
            )
          ]
        }
      );
    } });
  },
  { name: "BanksListController" }
);
const BanksListLoader = reactive.component(
  ({ data, services, monitors, children }) => {
    const { banks } = data;
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return children(handleExecute, banks, monitors, services);
  },
  {
    name: "BanksListLoader",
    queries: (_, services) => [
      {
        name: "banks",
        execute: (db) => db.withRequestBroker(services.banks.queryBanks.action, () => db.banks, {
          sorting: true,
          paging: false
        }).then(
          (banks) => banks.sort((a, b) => Number(!!b.pendingApproval) - Number(!!a.pendingApproval))
        )
      }
    ],
    monitors: (services) => [services.banks.queryBanks]
  }
);
const columns$5 = [
  { propertyName: "batchIdentity", name: "Batch", isSortable: true },
  { propertyName: "estimatedDepositDate", name: "Est. Deposit Date", isSortable: true },
  { propertyName: "depositDate", name: "Deposit Date", isSortable: true },
  { propertyName: "totalDepositAmount", name: "Deposit Amount", isSortable: true },
  { propertyName: "transactionCount", name: "# Transactions", isSortable: true },
  { propertyName: "transactionAmount", name: "Transactions $", isSortable: true },
  { propertyName: "verifiedTransactionCount", name: "Verified" },
  { propertyName: "reconciledDate", name: "Reconciled", isSortable: true }
];
const render$1 = (batch, columnKey) => {
  const cellValue = batch?.[columnKey];
  switch (columnKey) {
    case "estimatedDepositDate":
    case "depositDate":
      return cellValue ? parseAndFormatDate(cellValue, "utc", { format: DateTime.DATE_SHORT }) : "";
    case "totalDepositAmount":
    case "manualAdjustmentAmount":
    case "transactionAmount":
      return formatCurrencyUSD(cellValue || 0);
    case "transactionCount":
      return cellValue || 0;
    case "verifiedTransactionCount":
      return batch.transactionCount > 0 && cellValue === batch.transactionCount ? /* @__PURE__ */ jsx(RiCheckboxCircleFill, { className: "text-success" }) : "";
    case "reconciledDate":
      return cellValue ? /* @__PURE__ */ jsx(RiCheckboxCircleFill, { className: "text-success" }) : "";
    default:
      return cellValue;
  }
};
const BatchesList = ({ batches: batches2 }) => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(
    RequestBrokerTable,
    {
      selectionMode: "single",
      onRowAction: (key) => navigate(`${key}`),
      "aria-label": "Transaction Batches",
      columns: columns$5,
      items: batches2,
      renderColumn: (column) => /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, id: column.propertyName, children: column.name }, column.propertyName),
      renderRow: (batch) => /* @__PURE__ */ jsx(Table.Row, { className: "hover:cursor-pointer", id: batch.id, children: columns$5.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: render$1(batch, column.propertyName) }, `${batch.id}_${column.propertyName}`)) }, batch.id)
    }
  );
};
const filters$1 = new Filters([
  new FilterInput({
    name: "batchIdentity",
    title: "Batch Identity",
    options: {
      searchType: "contains"
    }
  }),
  new FilterRange({
    name: "totalDepositAmount",
    title: "Deposit Amount",
    minValue: 1e3,
    maxValue: 2e4,
    stepValue: 1e3,
    format: { style: "currency", currency: "USD" }
  }),
  new FilterDateRange({
    name: "estimatedDepositDate",
    title: "Est. Deposit Date"
  }),
  new FilterDateRange({
    name: "depositDate",
    title: "Deposit Date"
  }),
  new FilterDateRange({
    name: "reconciledDate",
    title: "Reconciled Date"
  })
]);
const sorting$1 = new Sorting(
  [
    { value: "batchIdentity", title: "Batch Identity" },
    { value: "totalDepositAmount", title: "Deposit Amount" },
    { value: "estimatedDepositDate", title: "Est. Deposit Date" },
    { value: "depositDate", title: "Deposit Date" }
  ],
  "batchIdentity",
  "asc"
);
const BatchesListController = reactive.component(
  ({ data, monitors, services, ...props }) => {
    const { batches: batches2 } = data;
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: services.batches.queryTransactionBatches.action,
        action: services.batches.queryTransactionBatches,
        filters: filters$1,
        sorting: sorting$1,
        isExecuting: monitors.queryTransactionBatches.executing,
        onExecute: handleExecute,
        children: /* @__PURE__ */ jsx(BatchesList, { batches: batches2, ...props })
      }
    );
  },
  {
    name: "BatchesListController",
    sync: (services) => [
      {
        execute: () => services.batches.queryTransactionBatches(),
        bypass: (db) => db.batches.count(),
        deps: []
      }
    ],
    queries: (_, services) => [
      {
        name: "batches",
        execute: (db) => db.withRequestBroker(services.batches.queryTransactionBatches.action, () => db.batches, {
          sorting: true
        })
      }
    ],
    monitors: (services) => [services.batches.queryTransactionBatches]
  }
);
const BatchesListView = () => {
  return /* @__PURE__ */ jsx(BatchesListController, {});
};
function CreateBatch({ createTransactionBatch, isLoading, isOpen, onCancel }) {
  const [form, setForm] = useState({});
  function handlePress() {
    createTransactionBatch({
      estimatedDepositDate: form?.data?.estimatedDepositDate,
      batchIdentity: "New manual batch"
    });
  }
  return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(Modal.Backdrop, { isOpen, children: /* @__PURE__ */ jsx(Modal.Container, { children: /* @__PURE__ */ jsxs(Modal.Dialog, { children: [
    /* @__PURE__ */ jsx(Modal.Header, { children: /* @__PURE__ */ jsx(Modal.Heading, { children: "New Batch" }) }),
    /* @__PURE__ */ jsx(Modal.Body, { children: /* @__PURE__ */ jsx(
      Uniformly,
      {
        name: "estimatedDepositDate",
        onChange: (form2) => {
          form2.data.estimatedDepositDate ? null : parseAndFormatDateTime(form2.data.estimatedDepositDate);
          setForm(form2);
        },
        children: /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
          DateTimeField,
          {
            variant: "faded",
            value: form?.data?.estimatedDepositDate,
            label: "Estimated deposit date",
            name: "estimatedDepositDate"
          }
        ) })
      }
    ) }),
    /* @__PURE__ */ jsxs(Modal.Footer, { className: "justify-end", children: [
      /* @__PURE__ */ jsx(ConfirmButton, { onConfirm: onCancel, body: "Any entered data will be lost", children: "Cancel" }),
      /* @__PURE__ */ jsxs(Button, { isPending: isLoading, onPress: handlePress, variant: "primary", children: [
        /* @__PURE__ */ jsx("i", { className: "text-lg ri-add-line" }),
        "Create"
      ] })
    ] })
  ] }) }) }) });
}
const CreateBatchController = reactive.component(
  ({ services, monitors }) => {
    const [isOpen, setIsOpen] = useState(true);
    const navigate = useNavigate();
    useSuccessReactor(services.batches.createTransactionBatch, (batch) => {
      setIsOpen(false);
      navigate(`../${batch.id}`);
    });
    return /* @__PURE__ */ jsx(
      CreateBatch,
      {
        isOpen,
        createTransactionBatch: services.batches.createTransactionBatch,
        isLoading: monitors.createTransactionBatch.isExecuting,
        onCancel: () => navigate(-1)
      }
    );
  },
  {
    name: "CreateBatchController",
    monitors: (services) => [services.batches.createTransactionBatch]
  }
);
function CreateBatchView() {
  return /* @__PURE__ */ jsx(CreateBatchController, {});
}
function BatchDetailsForm({ batch, monitors, updateTransactionBatch }) {
  const [form, setForm] = useState();
  return /* @__PURE__ */ jsxs(
    Uniformly,
    {
      className: "flex flex-col gap-4",
      name: "batch",
      onChange: (form2) => {
        form2.data.estimatedDepositDate = isEmpty(form2.data.estimatedDepositDate) ? null : toISO(form2.data.estimatedDepositDate);
        form2.data.depositDate = isEmpty(form2.data.depositDate) ? null : toISO(form2.data.depositDate);
        setForm(form2);
      },
      children: [
        /* @__PURE__ */ jsx(FieldStatus, { important: true, children: /* @__PURE__ */ jsx(TextField, { value: batch?.batchIdentity, name: "batchIdentity", label: "Batch identity" }) }),
        /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
          DateTimeField,
          {
            variant: "faded",
            value: batch?.estimatedDepositDate,
            name: "estimatedDepositDate",
            label: "Estimated deposit date"
          }
        ) }),
        /* @__PURE__ */ jsx(FieldStatus, { important: true, children: /* @__PURE__ */ jsx(
          DateTimeField,
          {
            variant: "faded",
            value: batch?.depositDate,
            name: "depositDate",
            label: "Deposit date"
          }
        ) }),
        /* @__PURE__ */ jsx(FieldStatus, { important: true, children: /* @__PURE__ */ jsx(
          TextField,
          {
            variant: "faded",
            value: batch?.totalDepositAmount,
            type: "number",
            name: "totalDepositAmount",
            label: "Bank Deposit Amount",
            startContent: "$"
          }
        ) }),
        /* @__PURE__ */ jsx(
          ResourceActions,
          {
            onSave: () => updateTransactionBatch(batch.id, { ...form?.data, id: batch.id }),
            isSavedEnabled: true,
            isLoading: monitors.updateTransactionBatch.executing
          }
        )
      ]
    }
  );
}
const FundingType = {
  CreditCard: 1,
  ACH: 2,
  Cash: 3,
  Check: 4,
  MoneyOrder: 5,
  Medicare: 6
};
const FundingTypeDisplay = {
  [FundingType.CreditCard]: "Credit Card",
  [FundingType.ACH]: "ACH",
  [FundingType.Cash]: "Cash",
  [FundingType.Check]: "Check",
  [FundingType.MoneyOrder]: "Money Order",
  [FundingType.Medicare]: "Medicare"
};
const FundingTypeIcon = {
  [FundingType.CreditCard]: /* @__PURE__ */ jsx(RiBankCardLine, { size: 16 }),
  [FundingType.ACH]: /* @__PURE__ */ jsx(RiBankLine, { size: 16 }),
  [FundingType.Cash]: /* @__PURE__ */ jsx(RiCashLine, { size: 16 }),
  [FundingType.Check]: /* @__PURE__ */ jsx(RiBillLine, { size: 16 }),
  [FundingType.MoneyOrder]: /* @__PURE__ */ jsx(RiMoneyDollarCircleLine, { size: 16 }),
  [FundingType.Medicare]: /* @__PURE__ */ jsx(RiHospitalLine, { size: 16 })
};
const FundingTypeOptions = [
  { value: FundingType.CreditCard, label: FundingTypeDisplay[FundingType.CreditCard] },
  { value: FundingType.ACH, label: FundingTypeDisplay[FundingType.ACH] },
  { value: FundingType.Cash, label: FundingTypeDisplay[FundingType.Cash] },
  { value: FundingType.Check, label: FundingTypeDisplay[FundingType.Check] },
  { value: FundingType.MoneyOrder, label: FundingTypeDisplay[FundingType.MoneyOrder] },
  { value: FundingType.Medicare, label: FundingTypeDisplay[FundingType.Medicare] }
];
const MerchantType = {
  PaySimple: 1,
  TSYS: 2,
  Cash: 3,
  Clover: 5,
  Fortis: 7
};
const MerchantTypeDisplay = {
  [MerchantType.PaySimple]: "PaySimple",
  [MerchantType.TSYS]: "TSYS",
  [MerchantType.Cash]: "Cash/Check",
  [MerchantType.Clover]: "Clover",
  [MerchantType.Fortis]: "Fortis"
};
const MerchantTypeIcon = {
  [MerchantType.PaySimple]: "ri-paypal-line",
  [MerchantType.TSYS]: "ri-tsys-line",
  [MerchantType.Cash]: "ri-cash-line",
  [MerchantType.Clover]: "ri-shopping-bag-3-line",
  [MerchantType.Fortis]: "ri-bank-card-line"
};
const MerchantTypeOptions = [
  { value: MerchantType.PaySimple, label: MerchantTypeDisplay[MerchantType.PaySimple] },
  { value: MerchantType.TSYS, label: MerchantTypeDisplay[MerchantType.TSYS] },
  { value: MerchantType.Cash, label: MerchantTypeDisplay[MerchantType.Cash] },
  { value: MerchantType.Clover, label: MerchantTypeDisplay[MerchantType.Clover] },
  { value: MerchantType.Fortis, label: MerchantTypeDisplay[MerchantType.Fortis] }
];
const ResponseCodeType = {
  CreditCard: 1,
  ACH: 2,
  AddressVerification: 3,
  SecurityCodeVerification: 4,
  Error: 5
};
const ResponseCodeTypeDisplay = {
  [ResponseCodeType.CreditCard]: "Credit Card",
  [ResponseCodeType.ACH]: "ACH",
  [ResponseCodeType.AddressVerification]: "Address Verification",
  [ResponseCodeType.SecurityCodeVerification]: "CVV Verification",
  [ResponseCodeType.Error]: "Error"
};
const ResponseCodeTypeOptions = [
  {
    value: ResponseCodeType.CreditCard,
    label: ResponseCodeTypeDisplay[ResponseCodeType.CreditCard]
  },
  {
    value: ResponseCodeType.ACH,
    label: ResponseCodeTypeDisplay[ResponseCodeType.ACH]
  },
  {
    value: ResponseCodeType.AddressVerification,
    label: ResponseCodeTypeDisplay[ResponseCodeType.AddressVerification]
  },
  {
    value: ResponseCodeType.SecurityCodeVerification,
    label: ResponseCodeTypeDisplay[ResponseCodeType.SecurityCodeVerification]
  },
  {
    value: ResponseCodeType.Error,
    label: ResponseCodeTypeDisplay[ResponseCodeType.Error]
  }
];
const ResponseCodeTypeIcon = {
  [ResponseCodeType.CreditCard]: "ri-bank-card-line",
  [ResponseCodeType.ACH]: "ri-bank-line",
  [ResponseCodeType.AddressVerification]: "ri-map-pin-2-line",
  [ResponseCodeType.SecurityCodeVerification]: "ri-shield-keyhole-line",
  [ResponseCodeType.Error]: "ri-error-warning-line"
};
const ResponseCodeTypeColor = {
  [ResponseCodeType.CreditCard]: "credit-card",
  [ResponseCodeType.ACH]: "ach",
  [ResponseCodeType.AddressVerification]: "address-verification",
  [ResponseCodeType.SecurityCodeVerification]: "security-code-verification",
  [ResponseCodeType.Error]: "error"
};
const TransactionStatusType = {
  Pending: 1,
  Processing: 2,
  Complete: 3
};
const TransactionResultType = {
  None: 0,
  Pending: 1,
  Approved: 2,
  Declined: 3,
  Error: 4,
  Voided: 5,
  Refunded: 6,
  PartiallyRefunded: 7
};
const TransactionStatusTypeDisplay = {
  [TransactionStatusType.Pending]: "Pending",
  [TransactionStatusType.Processing]: "Processing",
  [TransactionStatusType.Complete]: "Complete"
};
const TransactionStatusTypeColor = {
  [TransactionStatusType.Pending]: "warning",
  [TransactionStatusType.Processing]: "primary",
  [TransactionStatusType.Complete]: "success"
};
const TransactionStatusTypeIcon = {
  [TransactionStatusType.Pending]: "ri-hourglass-line",
  [TransactionStatusType.Processing]: "ri-loader-2-line",
  [TransactionStatusType.Complete]: "ri-checkbox-circle-line"
};
const TransactionResultTypeDisplay = {
  [TransactionResultType.None]: "None",
  [TransactionResultType.Pending]: "Pending",
  [TransactionResultType.Approved]: "Approved",
  [TransactionResultType.Declined]: "Declined",
  [TransactionResultType.Error]: "Error",
  [TransactionResultType.Voided]: "Voided",
  [TransactionResultType.Refunded]: "Refunded",
  [TransactionResultType.PartiallyRefunded]: "Partially Refunded"
};
const TransactionResultTypeIcon = {
  [TransactionResultType.None]: "ri-subtract-line",
  [TransactionResultType.Pending]: "ri-hourglass-line",
  [TransactionResultType.Approved]: "ri-checkbox-circle-line",
  [TransactionResultType.Declined]: "ri-close-circle-line",
  [TransactionResultType.Error]: "ri-error-warning-line",
  [TransactionResultType.Voided]: "ri-forbid-2-line",
  [TransactionResultType.Refunded]: "ri-refund-line",
  [TransactionResultType.PartiallyRefunded]: "ri-refund-2-line"
};
const TransactionResultTypeColor = {
  [TransactionResultType.None]: "none",
  [TransactionResultType.Pending]: "warning",
  [TransactionResultType.Approved]: "success",
  [TransactionResultType.Declined]: "danger",
  [TransactionResultType.Error]: "danger",
  [TransactionResultType.Voided]: "warning",
  [TransactionResultType.Refunded]: "secondary",
  [TransactionResultType.PartiallyRefunded]: "secondary"
};
const TransactionStatusTypeOptions = [
  {
    value: TransactionStatusType.Pending,
    label: TransactionStatusTypeDisplay[TransactionStatusType.Pending]
  },
  {
    value: TransactionStatusType.Processing,
    label: TransactionStatusTypeDisplay[TransactionStatusType.Processing]
  },
  {
    value: TransactionStatusType.Complete,
    label: TransactionStatusTypeDisplay[TransactionStatusType.Complete]
  }
];
const TransactionResultTypeOptions = [
  {
    value: TransactionResultType.None,
    label: TransactionResultTypeDisplay[TransactionResultType.None]
  },
  {
    value: TransactionResultType.Pending,
    label: TransactionResultTypeDisplay[TransactionResultType.Pending]
  },
  {
    value: TransactionResultType.Approved,
    label: TransactionResultTypeDisplay[TransactionResultType.Approved]
  },
  {
    value: TransactionResultType.Declined,
    label: TransactionResultTypeDisplay[TransactionResultType.Declined]
  },
  {
    value: TransactionResultType.Error,
    label: TransactionResultTypeDisplay[TransactionResultType.Error]
  },
  {
    value: TransactionResultType.Voided,
    label: TransactionResultTypeDisplay[TransactionResultType.Voided]
  },
  {
    value: TransactionResultType.Refunded,
    label: TransactionResultTypeDisplay[TransactionResultType.Refunded]
  },
  {
    value: TransactionResultType.PartiallyRefunded,
    label: TransactionResultTypeDisplay[TransactionResultType.PartiallyRefunded]
  }
];
const TargetPaymentType = {
  CreditCard: 1,
  ACH: 2,
  Refund: 3,
  Check: 5
};
const TargetPaymentTypeDisplay = {
  [TargetPaymentType.CreditCard]: "CreditCard",
  [TargetPaymentType.ACH]: "ACH",
  [TargetPaymentType.Refund]: "Refund",
  [TargetPaymentType.Check]: "Check"
};
const TransactionDetailSummary = ({ transaction, onSelectTransaction }) => {
  const monitor = useMonitor((services) => services.transactions.getTransaction);
  return /* @__PURE__ */ jsx(Table, { children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Transaction details", children: [
    /* @__PURE__ */ jsxs(Table.Header, { children: [
      /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, children: "" }),
      /* @__PURE__ */ jsx(Table.Column, { children: "" })
    ] }),
    /* @__PURE__ */ jsxs(Table.Body, { children: [
      /* @__PURE__ */ jsxs(Table.Row, { children: [
        /* @__PURE__ */ jsx(Table.Cell, { className: "max-w-[250px] w-[250px] text-primary", children: "Transaction ID" }),
        /* @__PURE__ */ jsx(Table.Cell, { className: "flex items-center gap-2", children: !monitor.complete ? /* @__PURE__ */ jsx(Skeleton, { className: "w-full rounded-md" }) : /* @__PURE__ */ jsxs(Fragment, { children: [
          transaction.id,
          " ",
          /* @__PURE__ */ jsx(CopyToClipboard, { text: transaction.id })
        ] }) })
      ] }),
      transaction.parentTransactionId && /* @__PURE__ */ jsxs(Table.Row, { children: [
        /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Parent ID" }),
        /* @__PURE__ */ jsx(Table.Cell, { className: "flex items-center gap-2", children: !monitor.complete ? /* @__PURE__ */ jsx(Skeleton, { className: "w-full rounded-md" }) : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            Link,
            {
              onPress: () => onSelectTransaction(transaction.parentTransactionId),
              href: "#",
              children: transaction.parentTransactionId
            }
          ),
          /* @__PURE__ */ jsx(CopyToClipboard, { text: transaction.parentTransactionId })
        ] }) })
      ] }),
      /* @__PURE__ */ jsxs(Table.Row, { children: [
        /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Timestamp" }),
        /* @__PURE__ */ jsx(Table.Cell, { children: !monitor.complete ? /* @__PURE__ */ jsx(Skeleton, { className: "w-full rounded-md" }) : parseAndFormatDateTime(transaction.transactionDateUtc) })
      ] }),
      /* @__PURE__ */ jsxs(Table.Row, { children: [
        /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Status" }),
        /* @__PURE__ */ jsx(Table.Cell, { children: !monitor.complete ? /* @__PURE__ */ jsx(Skeleton, { className: "w-full rounded-md" }) : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            "i",
            {
              className: `${TransactionStatusTypeIcon[transaction.statusType]} text-${TransactionStatusTypeColor[transaction.statusType]} pe-1`
            }
          ),
          transaction.statusTypeName
        ] }) })
      ] }),
      /* @__PURE__ */ jsxs(Table.Row, { children: [
        /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Result" }),
        /* @__PURE__ */ jsx(Table.Cell, { children: !monitor.complete ? /* @__PURE__ */ jsx(Skeleton, { className: "w-full rounded-md" }) : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            "i",
            {
              className: `${TransactionResultTypeIcon[transaction.resultType]} text-${TransactionResultTypeColor[transaction.resultType]} pe-1`
            }
          ),
          transaction.resultTypeName
        ] }) })
      ] }),
      /* @__PURE__ */ jsxs(Table.Row, { children: [
        /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Amount" }),
        /* @__PURE__ */ jsx(Table.Cell, { children: !monitor.complete ? /* @__PURE__ */ jsx(Skeleton, { className: "w-full rounded-md" }) : formatCurrencyUSD(transaction.amount) })
      ] }),
      /* @__PURE__ */ jsxs(Table.Row, { children: [
        /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Description" }),
        /* @__PURE__ */ jsx(Table.Cell, { children: !monitor.complete ? /* @__PURE__ */ jsx(Skeleton, { className: "w-full rounded-md" }) : transaction.description })
      ] })
    ] })
  ] }) }) });
};
const isBoolean = (data) => {
  return typeof data === "boolean" || data instanceof Boolean;
};
const isNumber = (data) => {
  return typeof data === "number" || data instanceof Number;
};
const isBigInt = (data) => {
  return typeof data === "bigint" || data instanceof BigInt;
};
const isDate = (data) => {
  return !!data && data instanceof Date;
};
const isString = (data) => {
  return typeof data === "string" || data instanceof String;
};
const isArray = (data) => {
  return Array.isArray(data);
};
const isObject = (data) => {
  return typeof data === "object" && data !== null;
};
const isFunction = (data) => {
  return !!data && data instanceof Object && typeof data === "function";
};
function quoteString(value, quoted) {
  if (quoted === void 0) {
    quoted = false;
  }
  return !value || quoted ? `"${value}"` : value;
}
function quoteStringValue(value, quoted, stringify) {
  if (stringify) {
    return JSON.stringify(value);
  }
  return quoted ? `"${value}"` : value;
}
function ExpandableObject(_ref) {
  let {
    field,
    value,
    data,
    lastElement,
    openBracket,
    closeBracket,
    level,
    style,
    shouldExpandNode,
    clickToExpandNode,
    outerRef,
    beforeExpandChange
  } = _ref;
  const shouldExpandNodeCalledRef = useRef(false);
  const [expanded, setExpanded] = useState(() => shouldExpandNode(level, value, field));
  const expanderButtonRef = useRef(null);
  useEffect(() => {
    if (!shouldExpandNodeCalledRef.current) {
      shouldExpandNodeCalledRef.current = true;
    } else {
      setExpanded(shouldExpandNode(level, value, field));
    }
  }, [shouldExpandNode]);
  const contentsId = useId();
  if (data.length === 0) {
    return EmptyObject({
      field,
      openBracket,
      closeBracket,
      lastElement,
      style
    });
  }
  const expanderIconStyle = expanded ? style.collapseIcon : style.expandIcon;
  const ariaLabel = expanded ? style.ariaLables.collapseJson : style.ariaLables.expandJson;
  const childLevel = level + 1;
  const lastIndex = data.length - 1;
  const setExpandWithCallback = (newExpandValue) => {
    if (expanded !== newExpandValue && (!beforeExpandChange || beforeExpandChange({
      level,
      value,
      field,
      newExpandValue
    }))) {
      setExpanded(newExpandValue);
    }
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      setExpandWithCallback(e.key === "ArrowRight");
    } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const direction = e.key === "ArrowUp" ? -1 : 1;
      if (!outerRef.current) return;
      const buttonElements = outerRef.current.querySelectorAll("[role=button]");
      let currentIndex = -1;
      for (let i = 0; i < buttonElements.length; i++) {
        if (buttonElements[i].tabIndex === 0) {
          currentIndex = i;
          break;
        }
      }
      if (currentIndex < 0) {
        return;
      }
      const nextIndex = (currentIndex + direction + buttonElements.length) % buttonElements.length;
      buttonElements[currentIndex].tabIndex = -1;
      buttonElements[nextIndex].tabIndex = 0;
      buttonElements[nextIndex].focus();
    }
  };
  const onClick = () => {
    var _outerRef$current;
    setExpandWithCallback(!expanded);
    const buttonElement = expanderButtonRef.current;
    if (!buttonElement) return;
    const prevButtonElement = (_outerRef$current = outerRef.current) === null || _outerRef$current === void 0 ? void 0 : _outerRef$current.querySelector('[role=button][tabindex="0"]');
    if (prevButtonElement) {
      prevButtonElement.tabIndex = -1;
    }
    buttonElement.tabIndex = 0;
    buttonElement.focus();
  };
  return /* @__PURE__ */ createElement("div", {
    className: style.basicChildStyle,
    role: "treeitem",
    "aria-expanded": expanded,
    "aria-selected": void 0
  }, /* @__PURE__ */ createElement("span", {
    className: expanderIconStyle,
    onClick,
    onKeyDown,
    role: "button",
    "aria-label": ariaLabel,
    "aria-expanded": expanded,
    "aria-controls": expanded ? contentsId : void 0,
    ref: expanderButtonRef,
    tabIndex: level === 0 ? 0 : -1
  }), (field || field === "") && (clickToExpandNode ? /* @__PURE__ */ createElement("span", {
    className: style.clickableLabel,
    onClick,
    onKeyDown
  }, quoteString(field, style.quotesForFieldNames), ":") : /* @__PURE__ */ createElement("span", {
    className: style.label
  }, quoteString(field, style.quotesForFieldNames), ":")), /* @__PURE__ */ createElement("span", {
    className: style.punctuation
  }, openBracket), expanded ? /* @__PURE__ */ createElement("ul", {
    id: contentsId,
    role: "group",
    className: style.childFieldsContainer
  }, data.map((dataElement, index) => /* @__PURE__ */ createElement(DataRender, {
    key: dataElement[0] || index,
    field: dataElement[0],
    value: dataElement[1],
    style,
    lastElement: index === lastIndex,
    level: childLevel,
    shouldExpandNode,
    clickToExpandNode,
    beforeExpandChange,
    outerRef
  }))) : /* @__PURE__ */ createElement("span", {
    className: style.collapsedContent,
    onClick,
    onKeyDown
  }), /* @__PURE__ */ createElement("span", {
    className: style.punctuation
  }, closeBracket), !lastElement && /* @__PURE__ */ createElement("span", {
    className: style.punctuation
  }, ","));
}
function EmptyObject(_ref2) {
  let {
    field,
    openBracket,
    closeBracket,
    lastElement,
    style
  } = _ref2;
  return /* @__PURE__ */ createElement("div", {
    className: style.basicChildStyle,
    role: "treeitem",
    "aria-selected": void 0
  }, (field || field === "") && /* @__PURE__ */ createElement("span", {
    className: style.label
  }, quoteString(field, style.quotesForFieldNames), ":"), /* @__PURE__ */ createElement("span", {
    className: style.punctuation
  }, openBracket), /* @__PURE__ */ createElement("span", {
    className: style.punctuation
  }, closeBracket), !lastElement && /* @__PURE__ */ createElement("span", {
    className: style.punctuation
  }, ","));
}
function JsonObject(_ref3) {
  let {
    field,
    value,
    style,
    lastElement,
    shouldExpandNode,
    clickToExpandNode,
    level,
    outerRef,
    beforeExpandChange
  } = _ref3;
  return ExpandableObject({
    field,
    value,
    lastElement: lastElement || false,
    level,
    openBracket: "{",
    closeBracket: "}",
    style,
    shouldExpandNode,
    clickToExpandNode,
    data: Object.keys(value).map((key) => [key, value[key]]),
    outerRef,
    beforeExpandChange
  });
}
function JsonArray(_ref4) {
  let {
    field,
    value,
    style,
    lastElement,
    level,
    shouldExpandNode,
    clickToExpandNode,
    outerRef,
    beforeExpandChange
  } = _ref4;
  return ExpandableObject({
    field,
    value,
    lastElement: lastElement || false,
    level,
    openBracket: "[",
    closeBracket: "]",
    style,
    shouldExpandNode,
    clickToExpandNode,
    data: value.map((element) => [void 0, element]),
    outerRef,
    beforeExpandChange
  });
}
function JsonPrimitiveValue(_ref5) {
  let {
    field,
    value,
    style,
    lastElement
  } = _ref5;
  let stringValue;
  let valueStyle = style.otherValue;
  if (value === null) {
    stringValue = "null";
    valueStyle = style.nullValue;
  } else if (value === void 0) {
    stringValue = "undefined";
    valueStyle = style.undefinedValue;
  } else if (isString(value)) {
    stringValue = quoteStringValue(value, !style.noQuotesForStringValues, style.stringifyStringValues);
    valueStyle = style.stringValue;
  } else if (isBoolean(value)) {
    stringValue = value ? "true" : "false";
    valueStyle = style.booleanValue;
  } else if (isNumber(value)) {
    stringValue = value.toString();
    valueStyle = style.numberValue;
  } else if (isBigInt(value)) {
    stringValue = `${value.toString()}n`;
    valueStyle = style.numberValue;
  } else if (isDate(value)) {
    stringValue = value.toISOString();
  } else if (isFunction(value)) {
    stringValue = "function() { }";
  } else {
    stringValue = value.toString();
  }
  return /* @__PURE__ */ createElement("div", {
    className: style.basicChildStyle,
    role: "treeitem",
    "aria-selected": void 0
  }, (field || field === "") && /* @__PURE__ */ createElement("span", {
    className: style.label
  }, quoteString(field, style.quotesForFieldNames), ":"), /* @__PURE__ */ createElement("span", {
    className: valueStyle
  }, stringValue), !lastElement && /* @__PURE__ */ createElement("span", {
    className: style.punctuation
  }, ","));
}
function DataRender(props) {
  const value = props.value;
  if (isArray(value)) {
    return /* @__PURE__ */ createElement(JsonArray, Object.assign({}, props));
  }
  if (isObject(value) && !isDate(value) && !isFunction(value)) {
    return /* @__PURE__ */ createElement(JsonObject, Object.assign({}, props));
  }
  return /* @__PURE__ */ createElement(JsonPrimitiveValue, Object.assign({}, props));
}
var styles = { "container-light": "_2IvMF _GzYRV", "basic-element-style": "_2bkNM", "child-fields-container": "_1BXBN", "label-light": "_1MGIk", "clickable-label-light": "_2YKJg _1MGIk _1MFti", "punctuation-light": "_3uHL6 _3eOF8", "value-null-light": "_2T6PJ", "value-undefined-light": "_1Gho6", "value-string-light": "_vGjyY", "value-number-light": "_1bQdo", "value-boolean-light": "_3zQKs", "value-other-light": "_1xvuR", "collapse-icon-light": "_oLqym _f10Tu _1MFti _1LId0", "expand-icon-light": "_2AXVT _f10Tu _1MFti _1UmXx", "collapsed-content-light": "_2KJWg _1pNG9 _1MFti", "container-dark": "_11RoI _GzYRV", "expand-icon-dark": "_17H2C _f10Tu _1MFti _1UmXx", "collapse-icon-dark": "_3QHg2 _f10Tu _1MFti _1LId0", "collapsed-content-dark": "_3fDAz _1pNG9 _1MFti", "label-dark": "_2bSDX", "clickable-label-dark": "_1RQEj _2bSDX _1MFti", "punctuation-dark": "_gsbQL _3eOF8", "value-null-dark": "_LaAZe", "value-undefined-dark": "_GTKgm", "value-string-dark": "_Chy1W", "value-number-dark": "_2bveF", "value-boolean-dark": "_2vRm-", "value-other-dark": "_1prJR" };
const defaultAriaLables = {
  collapseJson: "collapse JSON",
  expandJson: "expand JSON"
};
const defaultStyles = {
  container: styles["container-light"],
  basicChildStyle: styles["basic-element-style"],
  childFieldsContainer: styles["child-fields-container"],
  label: styles["label-light"],
  clickableLabel: styles["clickable-label-light"],
  nullValue: styles["value-null-light"],
  undefinedValue: styles["value-undefined-light"],
  stringValue: styles["value-string-light"],
  booleanValue: styles["value-boolean-light"],
  numberValue: styles["value-number-light"],
  otherValue: styles["value-other-light"],
  punctuation: styles["punctuation-light"],
  collapseIcon: styles["collapse-icon-light"],
  expandIcon: styles["expand-icon-light"],
  collapsedContent: styles["collapsed-content-light"],
  noQuotesForStringValues: false,
  quotesForFieldNames: false,
  ariaLables: defaultAriaLables,
  stringifyStringValues: false
};
const darkStyles = {
  container: styles["container-dark"],
  basicChildStyle: styles["basic-element-style"],
  childFieldsContainer: styles["child-fields-container"],
  label: styles["label-dark"],
  clickableLabel: styles["clickable-label-dark"],
  nullValue: styles["value-null-dark"],
  undefinedValue: styles["value-undefined-dark"],
  stringValue: styles["value-string-dark"],
  booleanValue: styles["value-boolean-dark"],
  numberValue: styles["value-number-dark"],
  otherValue: styles["value-other-dark"],
  punctuation: styles["punctuation-dark"],
  collapseIcon: styles["collapse-icon-dark"],
  expandIcon: styles["expand-icon-dark"],
  collapsedContent: styles["collapsed-content-dark"],
  noQuotesForStringValues: false,
  quotesForFieldNames: false,
  ariaLables: defaultAriaLables,
  stringifyStringValues: false
};
const allExpanded = () => true;
const JsonView = (_ref) => {
  let {
    data,
    style = defaultStyles,
    shouldExpandNode = allExpanded,
    clickToExpandNode = false,
    beforeExpandChange,
    compactTopLevel,
    ...ariaAttrs
  } = _ref;
  const outerRef = useRef(null);
  return /* @__PURE__ */ createElement("div", Object.assign({
    "aria-label": "JSON view"
  }, ariaAttrs, {
    className: style.container,
    ref: outerRef,
    role: "tree"
  }), compactTopLevel && isObject(data) ? Object.entries(data).map((_ref2) => {
    let [key, value] = _ref2;
    return /* @__PURE__ */ createElement(DataRender, {
      key,
      field: key,
      value,
      style: {
        ...defaultStyles,
        ...style
      },
      lastElement: true,
      level: 1,
      shouldExpandNode,
      clickToExpandNode,
      beforeExpandChange,
      outerRef
    });
  }) : /* @__PURE__ */ createElement(DataRender, {
    value: data,
    style: {
      ...defaultStyles,
      ...style
    },
    lastElement: true,
    level: 0,
    shouldExpandNode,
    clickToExpandNode,
    outerRef,
    beforeExpandChange
  }));
};
const TransactionDetailSections = ({
  transaction,
  onSelectTransaction,
  onSelectProfile,
  onSelectBatch
}) => {
  const hasChildren = transaction.children && Array.isArray(transaction.children) && transaction.children.length > 0;
  return /* @__PURE__ */ jsxs(Tabs, { children: [
    /* @__PURE__ */ jsx(Tabs.ListContainer, { children: /* @__PURE__ */ jsxs(Tabs.List, { "aria-label": "Transaction detail sections", children: [
      /* @__PURE__ */ jsxs(Tabs.Tab, { id: "details", children: [
        "Details",
        /* @__PURE__ */ jsx(Tabs.Indicator, {})
      ] }),
      /* @__PURE__ */ jsxs(Tabs.Tab, { id: "status", children: [
        "Status",
        /* @__PURE__ */ jsx(Tabs.Indicator, {})
      ] }),
      hasChildren && /* @__PURE__ */ jsxs(Tabs.Tab, { id: "children", children: [
        "Children",
        /* @__PURE__ */ jsx(Tabs.Indicator, {})
      ] }),
      /* @__PURE__ */ jsxs(Tabs.Tab, { id: "responses", children: [
        "Responses",
        /* @__PURE__ */ jsx(Tabs.Indicator, {})
      ] })
    ] }) }),
    /* @__PURE__ */ jsx(Tabs.Panel, { id: "details", className: "p-0", children: /* @__PURE__ */ jsx(Details, { transaction, onSelectProfile }) }),
    /* @__PURE__ */ jsx(Tabs.Panel, { id: "status", className: "p-0", children: /* @__PURE__ */ jsx(Status, { transaction, onSelectBatch }) }),
    hasChildren && /* @__PURE__ */ jsx(Tabs.Panel, { id: "children", className: "p-0", children: /* @__PURE__ */ jsx(Children, { transaction, onSelectTransaction }) }),
    /* @__PURE__ */ jsx(Tabs.Panel, { id: "responses", className: "p-0", children: /* @__PURE__ */ jsx(Responses, { transaction }) })
  ] });
};
const Details = ({ transaction, onSelectProfile }) => /* @__PURE__ */ jsx(Table, { children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Transaction details", children: [
  /* @__PURE__ */ jsxs(Table.Header, { children: [
    /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, children: "" }),
    /* @__PURE__ */ jsx(Table.Column, { children: "" })
  ] }),
  /* @__PURE__ */ jsxs(Table.Body, { children: [
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "max-w-[250px] w-[250px] text-primary", children: "Merchant" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: transaction.merchantName })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Merchant Transaction Code" }),
      /* @__PURE__ */ jsxs(Table.Cell, { className: "flex items-center gap-2", children: [
        transaction.remoteTransactionId,
        /* @__PURE__ */ jsx(CopyToClipboard, { text: transaction.remoteTransactionId })
      ] })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Auth Code" }),
      /* @__PURE__ */ jsxs(Table.Cell, { className: "flex items-center gap-2", children: [
        transaction.authorizationCode,
        /* @__PURE__ */ jsx(CopyToClipboard, { text: transaction.authorizationCode })
      ] })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Funding Type" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: transaction.fundingTypeName })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Transaction Type" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: transaction.transactionTypeName })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Payment Profile" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: /* @__PURE__ */ jsx(Link, { onPress: () => onSelectProfile(transaction.profileId), href: "#", children: transaction.profileName }) })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Amount" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: formatCurrencyUSD(transaction.amount) })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Fee Amount" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: formatCurrencyUSD(transaction.feeAmount) })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Net Amount" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: formatCurrencyUSD(transaction.feeAmount) })
    ] })
  ] })
] }) }) });
const Status = ({ transaction, onSelectBatch }) => /* @__PURE__ */ jsx(Table, { children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Transaction status details", children: [
  /* @__PURE__ */ jsxs(Table.Header, { children: [
    /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, children: "" }),
    /* @__PURE__ */ jsx(Table.Column, { children: "" })
  ] }),
  /* @__PURE__ */ jsxs(Table.Body, { children: [
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "max-w-[250px] w-[250px] text-primary", children: "Batch" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: transaction.batchId ? /* @__PURE__ */ jsx(Link, { onPress: () => onSelectBatch(transaction.batchId), href: "#", children: transaction.batchId }) : /* @__PURE__ */ jsx("span", { children: "Unassigned" }) })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Verified" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: transaction.isVerified ? /* @__PURE__ */ jsx("i", { className: "ri-checkbox-circle-fill text-success" }) : /* @__PURE__ */ jsx("i", { className: "ri-checkbox-blank-circle-fill text-danger" }) })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Verified On" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: transaction.verifiedOnUtc && parseAndFormatDateTime(transaction.verifiedOnUtc) })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Verified By" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: transaction.verifiedBy })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Reconciled" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: transaction.isReconciled ? /* @__PURE__ */ jsx("i", { className: "ri-checkbox-circle-fill text-success" }) : /* @__PURE__ */ jsx("i", { className: "ri-checkbox-blank-circle-fill text-danger" }) })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Reconciled On" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: transaction.reconciledOnUtc && parseAndFormatDateTime(transaction.reconciledOnUtc) })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Reconciled By" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: transaction.reconciledBy })
    ] })
  ] })
] }) }) });
const Children = ({ transaction, onSelectTransaction }) => /* @__PURE__ */ jsx(Table, { children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Child transaction", children: [
  /* @__PURE__ */ jsxs(Table.Header, { children: [
    /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, children: "Transaction Date" }),
    /* @__PURE__ */ jsx(Table.Column, { children: "Amount" }),
    /* @__PURE__ */ jsx(Table.Column, { children: "Funding Type" }),
    /* @__PURE__ */ jsx(Table.Column, { children: "Transaction Type" })
  ] }),
  /* @__PURE__ */ jsx(Table.Body, { items: transaction.children, children: (child) => /* @__PURE__ */ jsxs(Table.Row, { id: child.id, children: [
    /* @__PURE__ */ jsx(Table.Cell, { children: /* @__PURE__ */ jsx(Link, { onPress: () => onSelectTransaction(child.id), href: "#", children: parseAndFormatDateTime(child.transactionDateUtc) }) }),
    /* @__PURE__ */ jsx(Table.Cell, { children: formatCurrencyUSD(child.amount) }),
    /* @__PURE__ */ jsx(Table.Cell, { children: child.fundingTypeName }),
    /* @__PURE__ */ jsx(Table.Cell, { children: child.transactionTypeName })
  ] }) })
] }) }) });
const Responses = ({ transaction }) => {
  const theme = localStorage.getItem("theme");
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsx(Table, { children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Transaction responses", children: [
      /* @__PURE__ */ jsxs(Table.Header, { children: [
        /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, children: "Response Code" }),
        /* @__PURE__ */ jsx(Table.Column, { children: "Description" }),
        /* @__PURE__ */ jsx(Table.Column, { children: "Resolution" }),
        /* @__PURE__ */ jsx(Table.Column, { children: "Profile Frozen" })
      ] }),
      /* @__PURE__ */ jsx(Table.Body, { items: transaction.responses, children: (response) => /* @__PURE__ */ jsxs(Table.Row, { id: response.code, children: [
        /* @__PURE__ */ jsx(Table.Cell, { children: response.code }),
        /* @__PURE__ */ jsx(Table.Cell, { children: response.shortDescription }),
        /* @__PURE__ */ jsx(Table.Cell, { children: response.resolutionDescription }),
        /* @__PURE__ */ jsx(Table.Cell, { children: response.freezePayment ? "Yes" : "No" })
      ] }) })
    ] }) }) }),
    /* @__PURE__ */ jsxs(Card, { children: [
      /* @__PURE__ */ jsx(Card.Header, { children: "Raw Response" }),
      /* @__PURE__ */ jsx(Card.Content, { children: /* @__PURE__ */ jsx(
        JsonView,
        {
          data: JSON.parse(transaction.rawResponse || "{}"),
          shouldExpandNode: () => {
          },
          style: theme === "dark" ? darkStyles : defaultStyles
        }
      ) })
    ] })
  ] });
};
const RepublishTransactionEvent = reactive.component(
  ({ services, monitors, transaction }) => {
    if (!transaction?.id) return null;
    return /* @__PURE__ */ jsx(RequirePolicy, { resource: "transactions", operation: "update", children: /* @__PURE__ */ jsx(
      ConfirmableButton,
      {
        withConfirm: true,
        size: "sm",
        variant: "secondary",
        confirmColor: "primary",
        iconClass: "ri-send-plane-line",
        label: "Resend update event",
        isLoading: monitors.republishTransactionStatusUpdated.executing,
        body: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2 text-sm", children: [
          /* @__PURE__ */ jsx("p", { children: "Send this transaction's update event again? Billing and every other subscriber apply the transaction as it is stored now." }),
          /* @__PURE__ */ jsx("p", { className: "text-muted", children: "Use this when a subscriber missed or failed the original." })
        ] }),
        onPress: () => services.transactions.republishTransactionStatusUpdated(transaction.id)
      }
    ) });
  },
  {
    name: "RepublishTransactionEvent",
    monitors: (services) => [services.transactions.republishTransactionStatusUpdated]
  }
);
const TransactionDetailsController = reactive.component(
  ({ data, monitors, onSelectTransaction, onSelectProfile, onSelectBatch }) => {
    const { transaction, children } = data;
    const monitor = monitors.getTransaction;
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      monitor.complete && !isEmpty(transaction) && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("div", { className: "flex justify-end", children: /* @__PURE__ */ jsx(RepublishTransactionEvent, { transaction }) }),
        /* @__PURE__ */ jsx(
          TransactionDetailSummary,
          {
            transaction,
            onSelectTransaction
          }
        ),
        /* @__PURE__ */ jsx(
          TransactionDetailSections,
          {
            transaction: {
              ...transaction,
              children
            },
            onSelectTransaction,
            onSelectProfile,
            onSelectBatch
          }
        )
      ] }),
      monitor.complete && isEmpty(transaction) && /* @__PURE__ */ jsxs(Alert, { status: "warning", children: [
        /* @__PURE__ */ jsx(Alert.Indicator, {}),
        /* @__PURE__ */ jsxs(Alert.Content, { children: [
          /* @__PURE__ */ jsx(Alert.Title, { children: "No Transaction" }),
          /* @__PURE__ */ jsx(Alert.Description, { children: "Payment encountered an issue before a transaction was created" })
        ] })
      ] })
    ] });
  },
  {
    name: "TransactionDetailsController",
    sync: (services, { transactionId }) => [
      {
        execute: () => services.transactions.getTransaction(transactionId),
        deps: [transactionId]
      },
      {
        execute: () => services.transactions.getChildTransactions(transactionId),
        deps: [transactionId]
      }
    ],
    queries: ({ transactionId }) => [
      {
        name: "transaction",
        execute: (db) => db.payments.get(transactionId),
        defaultValue: {},
        deps: [transactionId]
      },
      {
        name: "children",
        execute: (db) => db.withRequestBroker(
          (services) => services.transactions.queryTransactions.action,
          () => db.payments.where({ parentTransactionId: transactionId }).toArray(),
          {
            sorting: true
          }
        ),
        defaultValue: [],
        deps: [transactionId]
      }
    ],
    monitors: (services) => [services.transactions.getTransaction]
  }
);
TransactionDetailsController.Modal = withModalController(({ onSuccess, onClose, ...props }) => /* @__PURE__ */ jsx(TransactionDetailsController, { onSuccess, onClose, ...props }));
const TransactionDetailsView = ({ transactionId, onClose, onSuccess }) => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(
    TransactionDetailsController.Modal,
    {
      isOpen: true,
      title: "Transaction Details",
      transactionId,
      onSelectTransaction: (id) => navigate(RoutePaths().transactions.details(id)),
      onSelectProfile: (id) => navigate(RoutePaths().profiles.details(id)),
      onSelectBatch: (id) => navigate(RoutePaths().batches.details(id)),
      onSuccess,
      onClose
    }
  );
};
const ACHTypeLookup = ({ name, label, value, selectionMode, ...rest }) => {
  return /* @__PURE__ */ jsx(
    SelectField,
    {
      name,
      label,
      items: [
        { value: 1, label: "Checking" },
        { value: 2, label: "Savings" }
      ],
      value,
      selectionMode: selectionMode || "single",
      emptyContent: "No options",
      ...rest,
      children: (accountType) => /* @__PURE__ */ jsxs(
        ListBox.Item,
        {
          id: accountType.value,
          textValue: String(accountType.label),
          children: [
            accountType.label,
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ]
        },
        accountType.value
      )
    }
  );
};
const FundingTypeLookup = reactive.component(
  ({ data, monitors, ...rest }) => {
    return /* @__PURE__ */ jsx(
      SelectField,
      {
        items: data.fundingTypes,
        isLoading: monitors.getFundingTypes.executing,
        ...rest,
        children: (fundingType) => /* @__PURE__ */ jsxs(
          ListBox.Item,
          {
            id: fundingType.value,
            textValue: fundingType.label,
            children: [
              fundingType.label,
              /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
            ]
          },
          fundingType.value
        )
      }
    );
  },
  {
    name: "FundingTypeLookup",
    sync: (services) => [
      {
        execute: () => services.fundingTypes.getFundingTypes(),
        deps: []
      }
    ],
    queries: () => [
      {
        name: "fundingTypes",
        execute: (db) => db.fundingTypes.toArray(),
        deps: [],
        transform: (fundingTypes) => fundingTypes?.map((fundingType) => ({
          value: fundingType.id,
          label: fundingType.name
        }))
      }
    ],
    monitors: (services) => [services.fundingTypes.getFundingTypes]
  }
);
const MerchantsFilter = (name, title, render2 = {}) => {
  return new FilterSelect({
    name,
    title,
    loader: () => new Promise((resolve, reject) => {
      const service = reactive.getServicesObject()["merchants"];
      if (service) {
        const reactor = service.queryMerchants.reactor("merchantsFilterReactor", {
          success: (response) => {
            resolve((response.payload.values ?? []).map((x) => ({ value: x.id, label: x.name })));
          }
        });
        reactor.connect();
        service.queryMerchants();
      } else {
        reject("Merchants service not found");
      }
    }),
    render: render2
  });
};
reactive.component(
  ({ monitors, data, selectionMode, value, hidden, ...rest }) => {
    const hiddenIds = (hidden || []).map((id) => String(id));
    return /* @__PURE__ */ jsx(
      AutocompleteField,
      {
        ...rest,
        value,
        items: data.merchants.filter((m) => !hiddenIds.includes(String(m.id))).map((m) => ({
          value: String(m.id ?? ""),
          label: String(m.name ?? m.id ?? "Unnamed Merchant")
        })),
        emptyContent: "No options",
        selectionMode: selectionMode || "single",
        isLoading: monitors.queryMerchants.executing,
        children: (item) => /* @__PURE__ */ jsxs(ListBox.Item, { id: item.value, textValue: item.label, children: [
          item.label,
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }, item.value)
      }
    );
  },
  {
    name: "MerchantsLookup",
    sync: (services) => [
      {
        execute: () => services.merchants.queryMerchants(),
        deps: []
      }
    ],
    queries: () => [
      {
        name: "merchants",
        execute: (db) => db.merchants.toArray(),
        defaultValue: [],
        deps: []
      }
    ],
    monitors: (services) => [services.merchants.queryMerchants]
  }
);
const MerchantTypeLookup = reactive.component(
  ({ data, monitors, name, label, value, selectionMode, ...rest }) => {
    return /* @__PURE__ */ jsx(
      SelectField,
      {
        name,
        label,
        items: data.merchantTypes,
        value,
        selectionMode: selectionMode || "single",
        isLoading: monitors.queryMerchantTypes.executing,
        ...rest,
        children: (merchantType) => /* @__PURE__ */ jsxs(ListBox.Item, { id: merchantType.id, textValue: merchantType.name, children: [
          merchantType.name,
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }, merchantType.id)
      }
    );
  },
  {
    name: "MerchantTypeLookup",
    sync: (services) => [
      {
        execute: () => services.merchantTypes.queryMerchantTypes(),
        deps: []
      }
    ],
    queries: () => [
      {
        name: "merchantTypes",
        execute: (db) => db.merchantTypes.toArray(),
        deps: []
      }
    ],
    monitors: (services) => [services.merchantTypes.queryMerchantTypes]
  }
);
reactive.component(
  ({ monitors, data, selectionMode, value, hidden, ...rest }) => {
    const hiddenIds = (hidden || []).map((id) => String(id));
    return /* @__PURE__ */ jsx(
      AutocompleteField,
      {
        ...rest,
        value,
        items: data.profiles.filter((p) => !hiddenIds.includes(String(p.id))).map((p) => ({
          value: String(p.id ?? ""),
          label: String(p.name ?? p.id ?? "Unnamed Profile")
        })),
        emptyContent: "No options",
        selectionMode: selectionMode || "single",
        isLoading: monitors.queryProfiles?.executing,
        children: (item) => /* @__PURE__ */ jsxs(ListBox.Item, { id: item.value, textValue: item.label, children: [
          item.label,
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }, item.value)
      }
    );
  },
  {
    name: "ProfilesLookup",
    sync: (services) => [
      {
        execute: () => services.profiles.queryProfiles(),
        deps: []
      }
    ],
    queries: () => [
      {
        name: "profiles",
        execute: (db) => db.profiles.toArray(),
        deps: []
      }
    ],
    monitors: (services) => [services.profiles.queryProfiles]
  }
);
const ResponseCodeTypesLookup = ({ selectionMode, ...rest }) => {
  return /* @__PURE__ */ jsx(
    SelectField,
    {
      items: ResponseCodeTypeOptions,
      selectionMode: selectionMode || "single",
      ...rest,
      children: (responseCodeType) => /* @__PURE__ */ jsxs(
        ListBox.Item,
        {
          id: responseCodeType.value,
          textValue: responseCodeType.label,
          children: [
            responseCodeType.label,
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ]
        },
        responseCodeType.value
      )
    }
  );
};
const TransactionResultTypeLookup = ({ name, label, value, selectionMode, ...rest }) => {
  return /* @__PURE__ */ jsx(
    SelectField,
    {
      name,
      label,
      items: TransactionResultTypeOptions,
      value,
      selectionMode: selectionMode || "single",
      ...rest,
      children: (resultType) => /* @__PURE__ */ jsxs(
        ListBox.Item,
        {
          id: resultType.value,
          textValue: String(resultType.label),
          children: [
            resultType.label,
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ]
        },
        resultType.value
      )
    }
  );
};
reactive.component(
  ({ monitors, data, selectionMode, value, name, ...rest }) => {
    const items = useMemo(() => {
      const items2 = batches.map((batch) => ({
        value: String(batch?.batchIdentity ?? ""),
        label: String(batch?.batchIdentity ?? batch?.id ?? "Unnamed Batch")
      }));
      items2.unshift({ value: null, label: "N/A" });
      return items2;
    }, [data.batches]);
    return /* @__PURE__ */ jsx(
      SelectField,
      {
        ...rest,
        name,
        value,
        items,
        selectionMode: selectionMode || "single",
        isLoading: monitors.queryTransactionBatches.executing,
        children: (batch) => /* @__PURE__ */ jsxs(ListBox.Item, { id: batch.value, textValue: String(batch.label), children: [
          batch.label,
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }, batch.value)
      }
    );
  },
  {
    name: "TransactionsBatchesLookup",
    sync: (services) => [
      {
        execute: () => services.batches.queryTransactionBatches(),
        deps: []
      }
    ],
    queries: () => [
      {
        name: "batches",
        execute: (db) => db.batches.toArray(),
        defaultValue: [],
        deps: []
      }
    ],
    monitors: (services) => [services.batches.queryTransactionBatches]
  }
);
reactive.component(
  ({ monitors, data, selectionMode, value, hidden, ...rest }) => {
    const hiddenValues = (hidden || []).map((v) => String(v));
    return /* @__PURE__ */ jsx(
      AutocompleteField,
      {
        ...rest,
        value,
        items: data.values.filter((v) => !hiddenValues.includes(String(v))).map((v) => ({ value: String(v), label: String(v) })),
        emptyContent: "No options",
        selectionMode: selectionMode || "single",
        isLoading: monitors.queryTransactions?.executing,
        children: (item) => /* @__PURE__ */ jsxs(ListBox.Item, { id: item.value, textValue: item.label, children: [
          item.label,
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }, item.value)
      }
    );
  },
  {
    name: "TransactionsFieldLookup",
    queries: (props) => [
      {
        name: "values",
        execute: async (db) => {
          const { field } = props;
          const rows = await db.transactions.toArray();
          const set = /* @__PURE__ */ new Set();
          for (const row of rows) {
            const v = row?.[field];
            if (v !== void 0 && v !== null && String(v).trim() !== "") set.add(v);
          }
          return Array.from(set);
        },
        deps: [props.field]
      }
    ],
    monitors: (services) => [services.transactions.queryTransactions]
  }
);
const sorting = new Sorting(
  [
    { value: "transactionDateUtc", title: "Date" },
    { value: "externalPaymentId", title: "Payment ID" },
    { value: "referenceId", title: "Reference ID" },
    { value: "description", title: "Description" },
    { value: "fundingTypeName", title: "Funding Type" },
    { value: "resultTypeName", title: "Result" },
    { value: "amount", title: "Amount" },
    { value: "merchantName", title: "Merchant" }
  ],
  "transactionDateUtc",
  "desc"
);
const filters = new Filters([
  new FilterDateRange({
    name: "transactionDateUtc",
    title: "Transaction Date"
  }),
  MerchantsFilter("merchantId", "Merchant"),
  new FilterSelect({
    name: "fundingTypeName",
    title: "Funding Type",
    loader: () => Promise.resolve(
      FundingTypeOptions.map((option) => ({
        value: option.label,
        label: option.label
      }))
    )
  }),
  new FilterSelect({
    name: "resultTypeName",
    title: "Result",
    loader: () => Promise.resolve(
      TransactionResultTypeOptions.map((option) => ({
        value: option.label,
        label: option.label
      }))
    )
  }),
  new FilterSelect({
    name: "statusTypeName",
    title: "Status",
    loader: () => Promise.resolve(
      TransactionStatusTypeOptions.map((option) => ({
        value: option.label,
        label: option.label
      }))
    )
  }),
  new FilterInput({
    name: "externalPaymentId",
    title: "Payment ID",
    options: {
      searchType: "equals"
    },
    render: {
      hideSearchType: true
    }
  }),
  new FilterInput({
    name: "referenceId",
    title: "Reference ID",
    options: {
      searchType: "equals"
    },
    render: {
      hideSearchType: true
    }
  }),
  new FilterInput({
    name: "remoteTransactionId",
    title: "Merchant Transaction ID",
    options: {
      searchType: "equals"
    },
    render: {
      hideSearchType: true
    }
  }),
  new FilterInput({
    name: "description",
    title: "Description",
    options: {
      searchType: "contains",
      isClearable: true
    }
  }),
  new FilterInput({
    name: "batchId",
    title: "Batch ID",
    options: {
      searchType: "equals"
    },
    render: {
      hideSearchType: true
    }
  }),
  new FilterRange({
    name: "amount",
    title: "Amount",
    minValue: 0,
    maxValue: 2e3,
    stepValue: 10,
    format: { style: "currency", currency: "USD" }
  }),
  new FilterToggle({
    name: "isReconciled",
    title: "Reconciled"
  }),
  new FilterToggle({
    name: "isVerified",
    title: "Verified"
  })
]);
const TransactionDescriptionCell = ({ description }) => {
  if (!description) return "";
  return /* @__PURE__ */ jsxs(Popover, { children: [
    /* @__PURE__ */ jsx(Popover.Trigger, { className: "max-w-64 cursor-pointer text-left", children: /* @__PURE__ */ jsx("span", { className: "block truncate", title: description, children: description }) }),
    /* @__PURE__ */ jsx(Popover.Content, { className: "max-w-md", children: /* @__PURE__ */ jsxs(Popover.Dialog, { children: [
      /* @__PURE__ */ jsx(Popover.Arrow, {}),
      /* @__PURE__ */ jsx("p", { className: "text-sm whitespace-pre-wrap break-words", children: description })
    ] }) })
  ] });
};
const columns$4 = [
  { key: "transactionDateUtc", propertyName: "transactionDateUtc", name: "Date", isSortable: true },
  {
    key: "externalPaymentId",
    propertyName: "externalPaymentId",
    name: "Payment ID",
    isSortable: true
  },
  {
    key: "referenceId",
    propertyName: "referenceId",
    name: "Reference ID",
    isSortable: true
  },
  {
    key: "remoteTransactionId",
    propertyName: "remoteTransactionId",
    name: "Merchant Transaction ID",
    isSortable: true
  },
  { key: "description", propertyName: "description", name: "Description", isSortable: true },
  { key: "fundingType", propertyName: "fundingTypeName", name: "Funding Type", isSortable: true },
  { key: "resultType", propertyName: "resultType", name: "Result", isSortable: true },
  { key: "amount", propertyName: "amount", name: "Amount", isSortable: true },
  { key: "merchantName", propertyName: "merchantName", name: "Merchant", isSortable: true }
];
const render = (row, columnKey, onSelectTransaction) => {
  const cellValue = row[columnKey];
  switch (columnKey) {
    case "batchId":
      return cellValue ? /* @__PURE__ */ jsx(Link$1, { to: RoutePaths().batches.details(cellValue), className: "hover:underline", children: cellValue }) : "";
    case "isReconciled":
      return /* @__PURE__ */ jsx("i", { className: cellValue ? "ri-check-line" : "" });
    case "isVerified":
      return /* @__PURE__ */ jsx("i", { className: cellValue ? "ri-check-line" : "" });
    case "resultType":
      return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("div", { className: `text-${TransactionResultTypeColor[cellValue]} rounded-md`, children: /* @__PURE__ */ jsx("i", { className: TransactionResultTypeIcon[cellValue] }) }),
        /* @__PURE__ */ jsx("p", { children: TransactionResultTypeDisplay[cellValue] })
      ] });
    case "statusTypeName":
      return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("div", { className: `text-${TransactionStatusTypeColor[row["statusType"]]} rounded-md`, children: /* @__PURE__ */ jsx("i", { className: TransactionStatusTypeIcon[row["statusType"]] }) }),
        /* @__PURE__ */ jsx("p", { children: cellValue })
      ] });
    case "amount":
    case "feeAmount":
    case "netAmount":
      return /* @__PURE__ */ jsx("p", { children: formatCurrencyUSD(cellValue ?? 0) });
    case "description":
      return /* @__PURE__ */ jsx(TransactionDescriptionCell, { description: cellValue });
    case "remoteTransactionId":
      if (!cellValue) return "";
      return cellValue;
    case "verifiedOnUtc":
    case "reconciledOnUtc": {
      return cellValue ? parseAndFormatDateTime(cellValue) : "";
    }
    case "transactionDateUtc": {
      return onSelectTransaction ? /* @__PURE__ */ jsx(
        Link,
        {
          onPress: (e) => {
            onSelectTransaction(row.id);
          },
          className: "cursor-pointer",
          children: parseAndFormatDateTime(cellValue)
        }
      ) : parseAndFormatDateTime(cellValue);
    }
    case "profileName":
      return /* @__PURE__ */ jsx(Link$1, { to: RoutePaths().profiles.details(row.profileId), className: "hover:underline", children: cellValue });
    default:
      return cellValue;
  }
};
const TransactionsTable = ({
  transactions = [],
  highlightFunction = () => {
  },
  headerContent,
  hideFilters = false,
  hideChips = false,
  onRowAction,
  onSelectTransaction,
  ...rest
}) => {
  return /* @__PURE__ */ jsx(
    RequestBrokerTable,
    {
      hideChips,
      hideFilters,
      columns: columns$4,
      items: transactions,
      renderColumn: (column) => /* @__PURE__ */ jsx(
        Table.Column,
        {
          isRowHeader: true,
          id: column.propertyName,
          align: column.propertyName === "actions" ? "center" : "start",
          allowsSorting: column?.isSortable,
          children: column.name
        },
        column.propertyName
      ),
      renderRow: (row) => /* @__PURE__ */ jsx(
        Table.Row,
        {
          className: `${highlightFunction(row) ? "bg-success/15 hover:bg-success-200" : ""}`,
          id: row.id,
          children: columns$4.map((column) => /* @__PURE__ */ jsx(Table.Cell, { className: "whitespace-nowrap", children: render(row, column.propertyName, onSelectTransaction) }, column.propertyName))
        },
        row.id
      ),
      classNames: {
        baseClassNames: {
          tr: onRowAction ? "hover:cursor-pointer" : ""
        }
      },
      topContent: headerContent && headerContent(),
      onRowAction,
      ...rest
    }
  );
};
const TransactionsListController = reactive.component(
  ({ data, services, monitors, onSelect }) => {
    const { transactions } = data;
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: services.transactions.queryTransactions.action,
        action: services.transactions.queryTransactions,
        filters,
        sorting,
        isExecuting: monitors.queryTransactions.executing,
        onExecute: handleExecute,
        children: /* @__PURE__ */ jsx(
          TransactionsTable,
          {
            selectionMode: "single",
            transactions,
            onRowAction: (key) => onSelect(key),
            onSelectTransaction: (key) => onSelect(key),
            headerContent: () => /* @__PURE__ */ jsxs("div", { className: "flex flex-row h-full flex-1 items-center justify-end px-2", children: [
              /* @__PURE__ */ jsx("div", { className: "grow" }),
              /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
                FilterPresets,
                {
                  presets: [
                    {
                      name: "Pending ACH",
                      values: [
                        { filterName: "fundingTypeName", value: "ACH" },
                        { filterName: "resultTypeName", value: "Pending" }
                      ]
                    }
                  ],
                  label: "Presets",
                  className: "min-w-[14rem]"
                }
              ) })
            ] })
          }
        )
      }
    );
  },
  {
    name: "TransactionsListController",
    queries: (_, services) => [
      {
        name: "transactions",
        execute: (db) => db.withRequestBroker(
          services.transactions.queryTransactions.action,
          () => db.transactions,
          {
            sorting: true
          }
        ),
        deps: []
      }
    ],
    monitors: (services) => [services.transactions.queryTransactions]
  }
);
const TransactionsListView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between mb-4 shrink-0", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Transactions" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { onPress: () => navigate(RoutePaths().transactions.list()), children: "History" })
    ] }) }),
    /* @__PURE__ */ jsx(
      TransactionsListController,
      {
        onSelect: (transactionId) => navigate(RoutePaths().transactions.details(transactionId))
      }
    ),
    /* @__PURE__ */ jsx(Outlet, {})
  ] });
};
const BatchTotals = ({ batch }) => /* @__PURE__ */ jsx(Table, { children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Batch transactions", children: [
  /* @__PURE__ */ jsxs(Table.Header, { children: [
    /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, className: "fit-content", children: "" }),
    /* @__PURE__ */ jsx(Table.Column, { children: "" })
  ] }),
  /* @__PURE__ */ jsxs(Table.Body, { children: [
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Batch ID" }),
      /* @__PURE__ */ jsxs(Table.Cell, { children: [
        batch.id,
        " ",
        /* @__PURE__ */ jsx(CopyToClipboard, { value: batch.id })
      ] })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "# Transactions" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: batch.transactionCount })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Transactions Total" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: formatCurrencyUSD(batch.transactionAmount) })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Fees Total" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: formatCurrencyUSD(batch.transactionFeeAmount) })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsxs(Table.Cell, { className: "text-primary", children: [
        "Difference",
        " ",
        /* @__PURE__ */ jsx(HelpIcon, { content: "Bank deposit - Transactions total + Fees total", size: 20 })
      ] }),
      /* @__PURE__ */ jsx(Table.Cell, { children: formatCurrencyUSD(batch.transactionAmountDifference) })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "# Verified" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: batch.verifiedTransactionCount })
    ] }),
    /* @__PURE__ */ jsxs(Table.Row, { children: [
      /* @__PURE__ */ jsx(Table.Cell, { className: "text-primary", children: "Verified Total" }),
      /* @__PURE__ */ jsx(Table.Cell, { children: formatCurrencyUSD(batch.verifiedTransactionAmount) })
    ] })
  ] })
] }) }) });
function BatchDetails({
  batch,
  transactions = [],
  monitors,
  updateTransactionBatch,
  bulkAssignTransactions,
  bulkUnassignTransactions,
  onSelectTransaction
}) {
  const navigate = useNavigate();
  const [selectedKeys, setSelectedKeys] = useState(/* @__PURE__ */ new Set());
  const broker = useRequestBroker();
  useSuccessReactor(bulkAssignTransactions, () => {
    broker.execute();
  });
  useSuccessReactor(bulkUnassignTransactions, () => {
    broker.execute();
  });
  return batch && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 h-full max-w-full", children: [
    /* @__PURE__ */ jsx(Accordion, { className: "shrink-0 p-0", variant: "surface", defaultExpandedKeys: ["0"], children: /* @__PURE__ */ jsxs(Accordion.Item, { id: "0", children: [
      /* @__PURE__ */ jsx(Accordion.Heading, { children: /* @__PURE__ */ jsxs(Accordion.Trigger, { className: "cursor-pointer", children: [
        `Batch ${batch?.id}`,
        /* @__PURE__ */ jsx(Accordion.Indicator, {})
      ] }) }),
      /* @__PURE__ */ jsx(Accordion.Panel, { children: /* @__PURE__ */ jsx(Accordion.Body, { children: /* @__PURE__ */ jsxs("div", { className: "flex flex-row w-full gap-4", children: [
        /* @__PURE__ */ jsx("div", { className: "grow", children: /* @__PURE__ */ jsx(
          BatchDetailsForm,
          {
            batch,
            monitors,
            updateTransactionBatch
          }
        ) }),
        /* @__PURE__ */ jsx("div", { className: "grow", children: batch && /* @__PURE__ */ jsx(BatchTotals, { batch }) })
      ] }) }) })
    ] }) }),
    /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-4 h-full overflow-hidden max-w-full", children: /* @__PURE__ */ jsx(
      TransactionsTable,
      {
        isHeaderSticky: true,
        transactions,
        onSelectTransaction,
        onSelectionChange: setSelectedKeys,
        selectionMode: "multiple",
        highlightFunction: (row) => row.batchId === batch.id,
        headerContent: () => /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx("div", { className: "flex grow items-center pe-1", children: /* @__PURE__ */ jsxs("div", { className: "flex grow items-center justify-end gap-1 ms-1", children: [
          /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                className: "rounded-full",
                variant: "primary",
                isIconOnly: true,
                isDisabled: selectedKeys.size === 0,
                isPending: monitors.bulkAssignTransactions.executing,
                onPress: () => bulkAssignTransactions(batch.id, {
                  transactionIds: Array.from(selectedKeys)
                }),
                children: /* @__PURE__ */ jsx("i", { className: "ri-bookmark-3-line" })
              }
            ),
            /* @__PURE__ */ jsx(Tooltip.Content, { children: "Assign transactions to batch" })
          ] }),
          /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                className: "rounded-full",
                variant: "danger",
                isIconOnly: true,
                isDisabled: selectedKeys.size === 0,
                isPending: monitors.bulkUnassignTransactions.executing,
                onPress: () => bulkUnassignTransactions({ transactionIds: Array.from(selectedKeys) }),
                children: /* @__PURE__ */ jsx("i", { className: "ri-bookmark-2-line" })
              }
            ),
            /* @__PURE__ */ jsx(Tooltip.Content, { children: "Remove transactions from batch" })
          ] }),
          /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                className: "rounded-full",
                variant: "primary",
                isIconOnly: true,
                onPress: () => navigate(RoutePaths().batches.reconciliation(batch.id)),
                children: /* @__PURE__ */ jsx("i", { className: "ri-list-check-3" })
              }
            ),
            /* @__PURE__ */ jsx(Tooltip.Content, { children: "Reconcile batch" })
          ] })
        ] }) }) })
      }
    ) })
  ] });
}
const RoutePaths = () => {
  const baseUrl = appRuntime.baseUrl;
  const rootPaths = {
    transactions: baseUrl,
    banks: baseUrl + "/banks",
    batches: baseUrl + "/batches",
    merchants: baseUrl + "/merchants",
    profiles: baseUrl + "/profiles",
    payments: baseUrl + "/payments",
    fundingTypes: baseUrl + "/funding-types",
    responseCodes: baseUrl + "/response-codes",
    responseCodeTypes: baseUrl + "/response-code-types"
  };
  return {
    transactions: {
      list: () => `${rootPaths.transactions}/view`,
      create: () => `${rootPaths.transactions}/create`,
      details: (id) => `${rootPaths.transactions}/view/${id}/details`,
      edit: (id) => `${rootPaths.transactions}/${id}/edit`
    },
    batches: {
      list: () => rootPaths.batches,
      create: () => `${rootPaths.batches}/create`,
      details: (id) => `${rootPaths.batches}/${id}`,
      transaction: (batchid, transactionId) => `${RoutePaths().batches.details(batchid)}/transactions/${transactionId}`,
      reconciliation: (id) => `${rootPaths.batches}/${id}/reconciliation`
    },
    banks: {
      list: () => rootPaths.banks,
      create: () => `${rootPaths.banks}/create`,
      update: (routingNumber) => `${rootPaths.banks}/${routingNumber}/update`
    },
    merchants: {
      list: () => rootPaths.merchants,
      create: () => `${rootPaths.merchants}/create`,
      update: (id) => `${rootPaths.merchants}/${id}/update`
    },
    profiles: {
      list: () => rootPaths.profiles,
      create: () => `${rootPaths.profiles}/create`,
      details: (id) => `${rootPaths.profiles}/${id}/details`,
      settings: (id) => `${rootPaths.profiles}/${id}/settings`
    },
    payments: {
      list: () => rootPaths.payments,
      create: () => `${rootPaths.payments}/create`,
      details: (id) => `${rootPaths.payments}/${id}/details`,
      edit: (id) => `${rootPaths.payments}/${id}/edit`,
      history: (id) => `${rootPaths.payments}/${id}/history`,
      imports: {
        list: () => `${rootPaths.payments}/imports`,
        create: () => `${rootPaths.payments}/imports/create`,
        details: (id) => `${rootPaths.payments}/imports/${id}/details`
      }
    },
    fundingTypes: {
      list: () => rootPaths.fundingTypes
    },
    responseCodes: {
      list: () => rootPaths.responseCodes,
      create: () => `${rootPaths.responseCodes}/create`,
      update: (id) => `${rootPaths.responseCodes}/${id}/update`
    },
    responseCodeTypes: {
      list: () => rootPaths.responseCodeTypes
    }
  };
};
const useBatchRecentView = (batch) => useRecentView({
  title: batch?.id ? batch.batchIdentity || `Batch #${batch.id}` : void 0,
  route: batch?.id ? RoutePaths().batches.details(batch.id) : void 0
});
const BatchDetailsController = reactive.component(
  ({ data, monitors, services, batchId, onSelectTransaction }) => {
    const { batch, transactions } = data;
    useBatchRecentView(batch?.[0]);
    const handleExecute = useCallback((action, metadata) => {
      action(metadata, batchId);
    }, []);
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: services.transactions.queryTransactions.action,
        action: services.transactions.queryTransactions,
        isExecuting: monitors.queryTransactions.executing,
        paging: new Paging(),
        filters,
        sorting,
        deps: [batchId],
        onExecute: handleExecute,
        children: /* @__PURE__ */ jsx(
          BatchDetails,
          {
            batch: batch[0],
            transactions,
            monitors,
            bulkAssignTransactions: services.batches.bulkAssignTransactions,
            bulkUnassignTransactions: services.batches.bulkUnassignTransactions,
            updateTransactionBatch: services.batches.updateTransactionBatch,
            onSelectTransaction
          }
        )
      }
    );
  },
  {
    name: "BatchDetailsController",
    queries: ({ batchId }) => [
      {
        name: "batch",
        execute: (db) => db.batches.where("id").equals(Number(batchId)).toArray()
      },
      {
        name: "transactions",
        execute: (db) => db.withRequestBroker(
          (services) => services.transactions.queryTransactions.action,
          () => db.transactions.toArray(),
          {
            sorting: true
          }
        ),
        defaultValue: []
      }
    ],
    monitors: (services) => [
      services.transactions.queryTransactions,
      services.batches.bulkAssignTransactions,
      services.batches.bulkUnassignTransactions,
      services.batches.updateTransactionBatch
    ]
  }
);
function BatchDetailsView() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      BatchDetailsController,
      {
        batchId,
        onSelectTransaction: (transactionId) => {
          console.log("Routing to: ", RoutePaths().batches.transaction(batchId, transactionId));
          navigate(RoutePaths().batches.transaction(batchId, transactionId));
        }
      }
    ),
    /* @__PURE__ */ jsx(Outlet, {})
  ] });
}
function FeeForm({ item, value, updateTransaction }) {
  const [form, setForm] = useState();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Uniformly, { onChange: setForm, children: /* @__PURE__ */ jsx(TextField, { name: "feeAmount", value, size: "sm", variant: "bordered", label: "Fee" }) }),
    /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          isIconOnly: true,
          variant: "primary",
          onPress: () => updateTransaction(item.id, {
            ...item,
            feeAmount: Number(form?.data?.feeAmount),
            netAmount: Number(item.amount) - Number(form?.data?.feeAmount)
          }),
          children: /* @__PURE__ */ jsx("i", { className: "text-lg ri-save-line" })
        }
      ),
      /* @__PURE__ */ jsx(Tooltip.Content, { children: "Save" })
    ] })
  ] });
}
function AmountsForm({ batch, updateTransactionBatch, reconciledTransactions }) {
  const [form, setForm] = useState();
  const reconciledAmount = reconciledTransactions.reduce((prev, curr) => prev + curr.netAmount, 0);
  return /* @__PURE__ */ jsxs(Uniformly, { onChange: setForm, className: "flex justify-between gap-4", children: [
    /* @__PURE__ */ jsx(
      TextField,
      {
        value: batch.totalDepositAmount,
        name: "statement",
        variant: "underlined",
        startContent: "$",
        label: "Statement balance"
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        value: reconciledAmount,
        name: "reconciled",
        variant: "underlined",
        startContent: "$",
        label: "Reconciled amount"
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        value: batch.manualAdjustmentAmount,
        name: "manual",
        variant: "underlined",
        startContent: "$",
        label: "Manual adjustment",
        endContent: /* @__PURE__ */ jsx(
          Button,
          {
            isIconOnly: true,
            variant: "primary",
            onPress: () => updateTransactionBatch(batch.id, {
              ...batch,
              manualAdjustmentAmount: form?.data?.manual
            }),
            children: /* @__PURE__ */ jsx("i", { className: "text-lg ri-save-line" })
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        value: Number(form?.data?.statement) - Number(form?.data?.reconciled) + Number(form?.data?.manual),
        name: "difference",
        variant: "underlined",
        startContent: "$",
        label: "Difference"
      }
    )
  ] });
}
const cols = [
  { key: "remoteTransactionId", name: "Remote ID" },
  { key: "transactionDateUtc", name: "Transaction Date" },
  { key: "authorizationCode", name: "Auth Code" },
  { key: "amount", name: "Gross" },
  { key: "feeAmount", name: "Fee" },
  { key: "netAmount", name: "Net" },
  { key: "isVerified", name: "Verified" },
  { key: "isReconciled", name: "Reconciled" }
];
function Reconciliation({
  batch,
  transactions,
  transactionsLoading,
  updateTransactionBatch,
  updateTransaction,
  transactionBatchRpc
}) {
  const navigate = useNavigate();
  const reconciledTransactions = transactions.filter((x) => x.isReconciled === true);
  useEffect(() => {
    if (transactions.length > 0) {
      reconciledTransactions.length === transactions.length && batch.reconciledDate === null && transactionBatchRpc(batch.id, "Reconcile");
    }
  }, [batch.id, batch.reconciledDate, transactionBatchRpc, transactions]);
  const renderCell = (item, columnKey) => {
    switch (columnKey) {
      case "isVerified":
        return /* @__PURE__ */ jsx("div", { className: "text-center", children: /* @__PURE__ */ jsx(
          Checkbox,
          {
            "aria-label": "Verified",
            onChange: (isSelected) => updateTransaction(item.id, { ...item, isVerified: isSelected }),
            defaultSelected: !!item?.[columnKey],
            children: /* @__PURE__ */ jsx(Checkbox.Control, { children: /* @__PURE__ */ jsx(Checkbox.Indicator, {}) })
          }
        ) });
      case "isReconciled":
        return /* @__PURE__ */ jsx("div", { className: "text-center", children: /* @__PURE__ */ jsx(
          Checkbox,
          {
            "aria-label": "Reconciled",
            onChange: (isSelected) => updateTransaction(item.id, { ...item, isReconciled: isSelected }),
            isDisabled: !item.isVerified,
            defaultSelected: !!item?.[columnKey],
            children: /* @__PURE__ */ jsx(Checkbox.Control, { className: "data-[selected=true]:bg-success data-[selected=true]:border-success", children: /* @__PURE__ */ jsx(Checkbox.Indicator, {}) })
          }
        ) });
      case "feeAmount":
        return /* @__PURE__ */ jsxs(Popover, { children: [
          /* @__PURE__ */ jsxs(Button, { isIconOnly: true, variant: "tertiary", className: "rounded-full", children: [
            "$",
            item?.[columnKey] ?? 0
          ] }),
          /* @__PURE__ */ jsx(Popover.Content, { children: /* @__PURE__ */ jsx(Popover.Dialog, { className: "p-2 flex flex-row gap-2", children: /* @__PURE__ */ jsx(
            FeeForm,
            {
              item,
              value: item?.[columnKey],
              updateTransaction
            }
          ) }) })
        ] });
      case "netAmount":
      case "amount":
        return "$" + (item?.[columnKey] ?? 0);
      case "transactionDateUtc":
        return parseAndFormatDateTime(item?.[columnKey]);
      default:
        return item?.[columnKey];
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col h-full gap-2 overflow-hidden", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex justify-between", children: [
      /* @__PURE__ */ jsxs(Button, { variant: "secondary", onPress: () => navigate(-1), children: [
        /* @__PURE__ */ jsx("i", { className: "text-lg ri-arrow-left-s-line" }),
        "Go back"
      ] }),
      /* @__PURE__ */ jsxs("span", { className: "text-xl", children: [
        "Transactions for batch ",
        batch.id,
        " ",
        batch.reconciledDate !== null && /* @__PURE__ */ jsx("i", { className: "text-xl ri-check-line text-success" })
      ] })
    ] }),
    /* @__PURE__ */ jsx(
      AmountsForm,
      {
        updateTransactionBatch,
        batch,
        reconciledTransactions
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "relative overflow-hidden", children: [
      /* @__PURE__ */ jsx(Table, { className: "overflow-hidden", children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Batch transactions", children: [
        /* @__PURE__ */ jsx(Table.Header, { columns: cols, className: "sticky top-0 z-10", children: (column) => /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, id: column.key, children: column.name }, column.key) }),
        /* @__PURE__ */ jsx(Table.Body, { items: transactions, children: (item) => /* @__PURE__ */ jsx(Table.Row, { id: item.id, children: cols.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(item, column.key) }, column.key)) }, item.id) })
      ] }) }) }),
      transactionsLoading && /* @__PURE__ */ jsx("div", { className: "absolute inset-0 flex items-center justify-center backdrop-blur-sm z-50", children: /* @__PURE__ */ jsx(Spinner, {}) })
    ] })
  ] });
}
const ReconciliationController = reactive.component(
  ({ services, data, monitors }) => {
    const { batch, transactions } = data;
    useBatchRecentView(batch);
    return /* @__PURE__ */ jsx(
      Reconciliation,
      {
        batch,
        transactions,
        transactionsLoading: monitors.queryTransactions.executing,
        updateTransaction: services.transactions.updateTransaction,
        updateTransactionBatch: services.batches.updateTransactionBatch,
        transactionBatchRpc: services.batches.rPC
      }
    );
  },
  {
    name: "ReconcilationController",
    sync: ({ batches: batches2, transactions }) => [
      { execute: () => batches2.queryTransactionBatches() },
      { execute: () => transactions.queryTransactions() }
    ],
    queries: ({ batchId }) => [
      {
        name: "batch",
        execute: (db) => db.batches.where("id").equals(Number(batchId)).first()
      },
      {
        name: "transactions",
        execute: (db) => db.transactions.where("batchId").equals(Number(batchId)).toArray()
      }
    ],
    monitors: ({ transactions }) => [transactions.queryTransactions]
  }
);
function ReconciliationView() {
  const { batchId } = useParams();
  return /* @__PURE__ */ jsx(ReconciliationController, { batchId });
}
const BatchesRouter = () => {
  const navigate = useNavigate();
  const { batchId } = useParams();
  return /* @__PURE__ */ jsxs(Routes, { children: [
    /* @__PURE__ */ jsx(Route, { path: "/", element: /* @__PURE__ */ jsx(BatchesListView, {}) }),
    /* @__PURE__ */ jsx(Route, { path: "/:batchId", element: /* @__PURE__ */ jsx(WithRouteParams, { element: BatchDetailsView }), children: /* @__PURE__ */ jsx(
      Route,
      {
        path: "transactions/:transactionId",
        element: /* @__PURE__ */ jsx(
          WithRouteParams,
          {
            element: TransactionDetailsView,
            onSuccess: () => navigate(-1),
            onClose: () => navigate(-1)
          }
        )
      }
    ) }),
    /* @__PURE__ */ jsx(Route, { path: "/create", element: /* @__PURE__ */ jsx(CreateBatchView, {}) }),
    /* @__PURE__ */ jsx(
      Route,
      {
        path: "/:batchId/reconciliation",
        element: /* @__PURE__ */ jsx(WithRouteParams, { element: ReconciliationView })
      }
    )
  ] });
};
const columns$3 = [
  { propertyName: "type", name: "Gateway", isSortable: true },
  { propertyName: "name", name: "Name", isSortable: true },
  { propertyName: "fundingType", name: "Funding Type", isSortable: true }
];
const MerchantsList = ({ merchants = [], onSelect }) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "fundingType":
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          FundingTypeIcon[cellValue],
          /* @__PURE__ */ jsx("p", { children: FundingTypeDisplay[cellValue] })
        ] });
      case "type":
        return MerchantTypeDisplay[cellValue];
      default:
        return cellValue || "N/A";
    }
  }, []);
  return /* @__PURE__ */ jsx(
    RequestBrokerTable,
    {
      "aria-label": "Merchant list",
      columns: columns$3,
      items: merchants,
      renderColumn: (column) => /* @__PURE__ */ jsx(
        Table.Column,
        {
          isRowHeader: true,
          id: column.propertyName,
          align: column.propertyName === "actions" ? "center" : "start",
          allowsSorting: column?.isSortable,
          children: column.name
        },
        column.propertyName
      ),
      renderRow: (row) => /* @__PURE__ */ jsx(
        Table.Row,
        {
          id: row.id,
          onAction: () => onSelect(row),
          className: "cursor-pointer",
          children: columns$3.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(row, column.propertyName) }, column.propertyName))
        },
        row.id
      )
    }
  );
};
const MerchantFiltersDefinition = new Filters([
  new FilterInput({
    name: "Name",
    title: "Name"
  }),
  new FilterSelect({
    name: "Type",
    title: "Merchant Type",
    render: {
      inputType: "integer"
    },
    loader: () => Promise.resolve(MerchantTypeOptions)
  }),
  new FilterSelect({
    name: "FundingType",
    title: "Funding Type",
    render: {
      inputType: "integer"
    },
    loader: () => Promise.resolve(FundingTypeOptions)
  })
]);
const MerchantsListController = reactive.component(
  ({ onSelect, data, services, monitors }) => {
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: services.merchants.queryMerchants.action,
        action: services.merchants.queryMerchants,
        filters: MerchantFiltersDefinition,
        isExecuting: monitors.queryMerchants.executing,
        onExecute: handleExecute,
        children: /* @__PURE__ */ jsx(
          MerchantsList,
          {
            merchants: data.merchants,
            isLoading: monitors.queryMerchants.executing,
            onSelect
          }
        )
      }
    );
  },
  {
    name: "MerchantsListController",
    queries: (_, services) => [
      {
        name: "merchants",
        execute: (db) => db.withRequestBroker(services.merchants.queryMerchants.action, () => db.merchants, {
          sorting: true
        })
      }
    ],
    monitors: (services) => [services.merchants.queryMerchants]
  }
);
const MerchantsListView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(MerchantsListController, { onSelect: (item) => navigate(RoutePaths().merchants.update(item.id)) }),
    /* @__PURE__ */ jsx(Outlet, {})
  ] });
};
const getKeyPlaceholders = (merchantType) => {
  switch (merchantType) {
    case MerchantType.PaySimple:
      return {
        key1: "Enter Username",
        key2: "Enter API Key",
        key3: "Enter Base URL"
      };
    case MerchantType.TSYS:
      return {
        key1: "Enter Merchant ID",
        key2: "Enter Registration Key",
        key3: "Enter Service URL"
      };
    case MerchantType.Clover:
      return {
        key1: "Enter Clover Merchant ID (mId)",
        key2: "Enter Ecommerce API Token (private)",
        key3: "Enter PAKMS Key (public)"
      };
    case MerchantType.Fortis:
      return {
        key1: "Enter User ID",
        key2: "Enter User API Key",
        key3: "Enter Developer ID",
        key4: "Enter Product Transaction ID (optional)"
      };
    default:
      return {
        key1: "Enter Key 1",
        key2: "Enter Key 2",
        key3: "Enter Key 3",
        key4: "Enter Key 4"
      };
  }
};
const MerchantForm = ({ merchant = {}, selected = {}, onChange }) => {
  const currentType = selected.type;
  const placeholders = getKeyPlaceholders(currentType);
  return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-4", onChange, children: [
    /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
      TextField,
      {
        name: "name",
        label: "Name",
        value: merchant.name,
        validation: (x) => x.string().max(100).required("Name is required"),
        variant: "faded"
      }
    ) }),
    /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
      MerchantTypeLookup,
      {
        name: "type",
        type: "number",
        label: "Merchant Type",
        value: merchant.type,
        validation: (x) => x.number().required("Merchant Type is required"),
        variant: "faded"
      }
    ) }),
    /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
      FundingTypeLookup,
      {
        name: "fundingType",
        type: "number",
        label: "Funding Type",
        value: merchant.fundingType,
        validation: (x) => x.number().required("Funding Type is required"),
        variant: "faded"
      }
    ) }),
    /* @__PURE__ */ jsx(FieldStatus, { important: true, children: /* @__PURE__ */ jsx(
      PasswordField,
      {
        name: "key1",
        label: "Key 1",
        placeholder: placeholders.key1,
        value: merchant.key1,
        validation: (x) => x.string().max(255),
        variant: "faded"
      }
    ) }),
    /* @__PURE__ */ jsx(FieldStatus, { important: true, children: /* @__PURE__ */ jsx(
      PasswordField,
      {
        name: "key2",
        label: "Key 2",
        placeholder: placeholders.key2,
        value: merchant.key2,
        validation: (x) => x.string().max(255),
        variant: "faded"
      }
    ) }),
    /* @__PURE__ */ jsx(FieldStatus, { important: true, children: /* @__PURE__ */ jsx(
      PasswordField,
      {
        name: "key3",
        label: "Key 3",
        placeholder: placeholders.key3,
        value: merchant.key3,
        validation: (x) => x.string().max(255),
        variant: "faded"
      }
    ) }),
    /* @__PURE__ */ jsx(FieldStatus, { children: /* @__PURE__ */ jsx(
      PasswordField,
      {
        name: "key4",
        label: "Key 4",
        placeholder: placeholders.key4 ?? "Enter Key 4",
        value: merchant.key4,
        validation: (x) => x.string().max(255),
        variant: "faded"
      }
    ) })
  ] });
};
const MerchantCreateController = reactive.component(
  ({ services, monitors, onSuccess, onClose }) => {
    const [form, setForm] = useState({});
    useSuccessReactor(services.merchants.createMerchant, (payload) => {
      onSuccess && onSuccess(payload);
    });
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(MerchantForm, { selected: form.data, merchant: form?.data, onChange: setForm }),
      /* @__PURE__ */ jsx(
        ResourceActions,
        {
          onSave: () => services.merchants.createMerchant(form.data),
          onCancel: onClose,
          isSaveEnabled: form.isValid,
          isLoading: monitors.createMerchant.executing
        }
      )
    ] });
  },
  {
    name: "MerchantCreateController",
    monitors: (services) => [services.merchants.createMerchant]
  }
);
MerchantCreateController.Modal = withModalController(MerchantCreateController);
const MerchantCreateView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(
    MerchantCreateController.Modal,
    {
      title: "Create Merchant",
      isOpen: true,
      isDismissable: false,
      onClose: () => navigate(RoutePaths().merchants.list()),
      onSuccess: () => navigate(RoutePaths().merchants.list())
    }
  );
};
const MerchantUpdateController = reactive.component(
  ({ services, monitors, data, onSuccess, onClose }) => {
    const [form, setForm] = useState({});
    const { merchant } = data;
    const { data: merchantUpdated } = form;
    useSuccessReactor(services.merchants.updateMerchant, (payload) => {
      onSuccess && onSuccess(payload);
    });
    useSuccessReactor(services.merchants.deleteMerchant, () => {
      onClose && onClose();
    });
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(MerchantForm, { selected: form.data, merchant, onChange: setForm }),
      /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-end gap-x-2", children: /* @__PURE__ */ jsx(
        ResourceActions,
        {
          onArchive: () => services.merchants.deleteMerchant(merchant.id),
          onSave: () => services.merchants.updateMerchant(merchant.id, {
            id: merchant.id,
            ...merchantUpdated
          }),
          onCancel: onClose,
          isSaveEnabled: form.isValid,
          isLoading: monitors.deleteMerchant.executing || monitors.updateMerchant.executing
        }
      ) })
    ] });
  },
  {
    name: "MerchantUpdateController",
    sync: (services, { merchantId }) => [
      {
        execute: () => services.merchants.getMerchant(merchantId),
        deps: [merchantId]
      }
    ],
    queries: ({ merchantId }) => [
      {
        name: "merchant",
        execute: (db) => db.merchants.get(merchantId),
        deps: [merchantId]
      }
    ],
    monitors: (services) => [
      services.merchants.updateMerchant,
      services.merchants.deleteMerchant,
      services.merchants.getMerchant
    ]
  }
);
MerchantUpdateController.Modal = withModalController(({ onSuccess, onDelete, ...props }) => /* @__PURE__ */ jsx(MerchantUpdateController, { onSuccess, onDelete, ...props }));
const MerchantUpdateView = () => {
  const navigate = useNavigate();
  const { merchantId } = useParams();
  return /* @__PURE__ */ jsx(
    MerchantUpdateController.Modal,
    {
      title: "Update Merchant",
      isOpen: true,
      isDismissable: false,
      merchantId: Number(merchantId),
      onClose: () => navigate(RoutePaths().merchants.list()),
      onSuccess: () => navigate(RoutePaths().merchants.list())
    }
  );
};
const WithParams = ({ element: Component }) => {
  const params = useParams();
  return /* @__PURE__ */ jsx(Component, { ...params });
};
const MerchantsRouter = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsxs(Route, { path: "/", element: /* @__PURE__ */ jsx(MerchantsListView, {}), children: [
  /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(MerchantCreateView, {}) }),
  /* @__PURE__ */ jsx(Route, { path: ":merchantId/update", element: /* @__PURE__ */ jsx(WithParams, { element: MerchantUpdateView }) })
] }) });
const PaymentImportFilterDefinitions = new Filters([
  new FilterInput({
    name: "name",
    title: "Search by Name",
    defaultValue: "",
    options: {
      scope: "custom",
      searchType: "contains"
    },
    render: {
      hideContainer: true
    }
  }),
  new FilterInput({
    name: "name",
    title: "Name",
    options: {
      searchType: "contains"
    }
  }),
  new FilterDateRange({
    name: "createdOnUTC",
    title: "Created Date"
  })
]);
const PaymentImportDetailFilterDefinitions = new Filters([
  new FilterConstant({
    name: "paymentImportId",
    title: "Payment Import",
    render: {
      inputType: "number"
    }
  }),
  new FilterInput({
    name: "payorName",
    title: "Payor Name",
    options: {
      searchType: "contains"
    }
  }),
  new FilterInput({
    name: "customerName",
    title: "Customer Name",
    options: {
      searchType: "contains"
    }
  }),
  new FilterInput({
    name: "targetPaymentType",
    title: "Payment Type",
    options: {
      searchType: "contains"
    }
  }),
  new FilterDateRange({
    name: "paymentDateUTC",
    title: "Payment Date"
  })
]);
const PaymentImportColumnDefinitions = [
  { propertyName: "name", name: "Name", isSortable: true },
  { propertyName: "createdBy", name: "User", isSortable: true },
  { propertyName: "createdOnUTC", name: "Date", isSortable: true },
  { propertyName: "count", name: "Imported", isSortable: true },
  { propertyName: "consolidatedCount", name: "Completed", isSortable: true },
  { propertyName: "totalAppliedAmount", name: "Applied", isSortable: true },
  { propertyName: "totalConsolidatedAmount", name: "Completed $", isSortable: true },
  { propertyName: "missing", name: "Missing", isSortable: true }
];
const PaymentImportDetailsColumnDefinitions = [
  { propertyName: "payorId", name: "Payor #", isSortable: true },
  { propertyName: "payorName", name: "Payor Name", isSortable: true },
  { propertyName: "customerId", name: "Customer #", isSortable: true },
  { propertyName: "customerName", name: "Customer Name", isSortable: true },
  { propertyName: "targetPaymentType", name: "Payment Type", isSortable: true },
  { propertyName: "paymentDateUTC", name: "Payment Date", isSortable: true },
  { propertyName: "transactionId", name: "Transaction", isSortable: true },
  { propertyName: "checkNumber", name: "Check Number", isSortable: true },
  { propertyName: "bankAccountNumber", name: "Bank Account #", isSortable: true },
  { propertyName: "depositDateUTC", name: "Deposit Date", isSortable: true },
  { propertyName: "amount", name: "Amount", isSortable: true },
  { propertyName: "appliedAmount", name: "Applied Amount", isSortable: true },
  { propertyName: "isSplitPayment", name: "Split Payment", isSortable: true },
  { propertyName: "checkMemo", name: "Check Memo", isSortable: true },
  { propertyName: "isActiveSubscription", name: "Active Subscription", isSortable: true },
  { propertyName: "customerNote", name: "Customer Note", isSortable: true },
  { propertyName: "correlationId", name: "Correlation #", isSortable: true },
  { propertyName: "createOrder", name: "Create Order", isSortable: true },
  {
    propertyName: "updateSubscriptionAmount",
    name: "Update Subscription Amount",
    isSortable: true
  },
  { propertyName: "error", name: "", isSortable: false }
];
const PaymentImportsTable = ({ paymentImports, onSelect }) => {
  const renderCell = (paymentImport, columnKey) => {
    const cellValue = paymentImport?.[columnKey];
    const missing = paymentImport.totalAppliedAmount - paymentImport.totalConsolidatedAmount;
    switch (columnKey) {
      case "createdOnUTC":
        return cellValue ? parseAndFormatDateTime(cellValue) : "";
      case "totalAppliedAmount":
      case "totalConsolidatedAmount":
        return formatCurrencyUSD(cellValue || 0);
      case "missing":
        return /* @__PURE__ */ jsx(
          "span",
          {
            className: `font-semibold ${missing > 0 ? "text-danger" : missing < 0 ? "text-warning" : "text-success"}`,
            children: formatCurrencyUSD(missing)
          }
        );
      case "count":
      case "consolidatedCount":
        return cellValue || 0;
      default:
        return cellValue;
    }
  };
  return /* @__PURE__ */ jsx(
    RequestBrokerTable,
    {
      selectionMode: "single",
      "aria-label": "Payment Imports Table",
      columns: PaymentImportColumnDefinitions,
      items: paymentImports,
      renderColumn: (column) => /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, id: column.propertyName, children: column.name }, column.propertyName),
      renderRow: (paymentImport) => /* @__PURE__ */ jsx(
        Table.Row,
        {
          className: "hover:cursor-pointer",
          id: paymentImport.id,
          onPress: () => onSelect && onSelect(paymentImport),
          children: PaymentImportColumnDefinitions.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(paymentImport, column.propertyName) }, `${paymentImport.id}_${column.propertyName}`))
        },
        paymentImport.id
      )
    }
  );
};
const PaymentImportsListController = reactive.component(
  ({ services, data, monitors, db, onSelect }) => {
    const { paymentImports } = data;
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: services.paymentImports.queryPaymentImports.action,
        action: services.paymentImports.queryPaymentImports,
        sorting: new Sorting(
          [
            {
              value: "createdOnUTC",
              title: "Date Created"
            }
          ],
          "createdOnUTC",
          "desc"
        ),
        paging: new Paging(),
        filters: PaymentImportFilterDefinitions,
        isExecuting: monitors.queryPaymentImports.executing,
        onExecute: handleExecute,
        deps: [],
        children: /* @__PURE__ */ jsx("div", { className: "flex flex-col w-full h-full gap-2", children: /* @__PURE__ */ jsx(
          PaymentImportsTable,
          {
            paymentImports,
            onSelect: (item) => onSelect && onSelect(item)
          }
        ) })
      }
    );
  },
  {
    name: "PaymentImportsListController",
    queries: (_, services) => [
      {
        name: "paymentImports",
        execute: (db) => db.withRequestBroker(
          services.paymentImports.queryPaymentImports.action,
          () => db.payment_imports,
          {
            sorting: true
          }
        ),
        deps: []
      }
    ],
    monitors: (services) => [services.paymentImports.queryPaymentImports]
  }
);
const PaymentImportsView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 w-full h-full", children: [
    /* @__PURE__ */ jsx("div", { className: "flex justify-between items-center", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Payments" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Imports" })
    ] }) }),
    /* @__PURE__ */ jsx(
      PaymentImportsListController,
      {
        onSelect: (paymentImport) => navigate(RoutePaths().payments.imports.details(paymentImport.id))
      }
    ),
    /* @__PURE__ */ jsx(Outlet, {})
  ] });
};
const PaymentImportForm = ({ onChange }) => {
  return /* @__PURE__ */ jsxs(
    Uniformly,
    {
      className: "flex flex-col gap-2",
      onChange: (form) => {
        onChange && onChange({
          ...form,
          data: {
            ...form.data,
            attachment: form.data.attachment !== "" ? form.data.attachment[0] : null
          }
        });
      },
      children: [
        /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
          TextField,
          {
            name: "name",
            label: "Name",
            validation: (x) => x.string().required("Name is required")
          }
        ) }),
        /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
          AttachmentField,
          {
            name: "attachment",
            label: "Upload Payment CSV",
            kind: "csv",
            size: "sm",
            validation: (x) => x.array().min(1, "At least one attachment is required")
          }
        ) })
      ]
    }
  );
};
const PaymentImportDetailsTotal = ({ payments, isValid }) => {
  const totalAmount = payments.reduce(
    (total, payment) => total + payment.appliedAmount,
    0
  );
  return /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-3", children: /* @__PURE__ */ jsxs(Grid, { cols: 3, className: "gap-3", children: [
    /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(Card, { className: "shadow-xs border border-default-200", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-1 p-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 text-default-500 text-xs", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-shield-check-line text-base" }),
        "Status"
      ] }),
      /* @__PURE__ */ jsx(
        "span",
        {
          className: `text-lg font-semibold ${isValid ? "text-success" : "text-danger"}`,
          children: isValid ? "Valid" : "Invalid"
        }
      )
    ] }) }) }),
    /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(Card, { className: "shadow-xs border border-default-200", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-1 p-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 text-default-500 text-xs", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-file-list-3-line text-base" }),
        "Payments"
      ] }),
      /* @__PURE__ */ jsx("span", { className: "text-lg font-semibold text-default-900", children: payments.length })
    ] }) }) }),
    /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(Card, { className: "shadow-xs border border-default-200", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-1 p-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 text-default-500 text-xs", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-money-dollar-circle-line text-base" }),
        "Total Amount"
      ] }),
      /* @__PURE__ */ jsx("span", { className: "text-lg font-semibold text-primary", children: formatCurrencyUSD(totalAmount) })
    ] }) }) })
  ] }) });
};
const PaymentImportErrorsAndWarningsList = ({ errors, warnings }) => {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
    errors.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-error-warning-line text-danger" }),
        /* @__PURE__ */ jsx("span", { className: "font-semibold", children: "Errors" })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-1", children: errors.map((error, index) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-error-warning-line text-danger" }),
        /* @__PURE__ */ jsx("span", { children: error })
      ] }, index)) })
    ] }),
    warnings.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-alert-line text-warning" }),
        /* @__PURE__ */ jsx("span", { className: "font-semibold", children: "Warnings" })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-1", children: warnings.map((warning, index) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-alert-line text-warning" }),
        /* @__PURE__ */ jsx("span", { children: warning })
      ] }, index)) })
    ] })
  ] });
};
const downloadTemplate = () => {
  const headers = [
    "PayorId",
    "PayorName",
    "CustomerId",
    "CustomerName",
    "OriginalPaymentType",
    "TargetPaymentType",
    "PaymentDateUTC",
    "TransactionId",
    "CheckNumber",
    "BankAccountNumber",
    "DepositDateUTC",
    "Amount",
    "AppliedAmount",
    "IsSplitPayment",
    "CheckMemo",
    "IsActiveSubscription",
    "CustomerNote",
    "CreateOrder",
    "UpdateSubscriptionAmount"
  ];
  const exampleRow = [
    "3004",
    "Mia Delgado",
    "7204",
    "Pacific Retail Group",
    "4",
    "5",
    "2026-02-17T16:20:00",
    "",
    "CHK30034",
    "",
    "2026-02-17T18:35:00",
    "72.95",
    "72.95",
    "false",
    "Store maintenance fee",
    "false",
    "No remarks",
    "false",
    "false"
  ];
  const csv = headers.join(",") + "\n" + exampleRow.join(",") + "\n";
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "payment-import-template.csv";
  a.click();
  URL.revokeObjectURL(url);
};
const downloadReadme = () => {
  const columns2 = [
    {
      name: "PayorId",
      type: "long",
      description: "Unique identifier for the entity making the payment."
    },
    { name: "PayorName", type: "string", description: "The legal or display name of the payor." },
    {
      name: "CustomerId",
      type: "long",
      description: "Unique identifier for the customer receiving the credit."
    },
    {
      name: "CustomerName",
      type: "string",
      description: "The legal or display name of the customer."
    },
    {
      name: "OriginalPaymentType",
      type: "int",
      description: "Numeric code representing the source payment method.",
      values: [
        { code: 3, label: "Refund" },
        { code: 5, label: "Check" }
      ]
    },
    {
      name: "TargetPaymentType",
      type: "Enum",
      description: "The PaymentTypeRequest classification for processing.",
      values: [
        { code: 1, label: "CreditCard" },
        { code: 2, label: "ACH" },
        { code: 3, label: "Refund" },
        { code: 5, label: "Check" }
      ]
    },
    {
      name: "PaymentDateUTC",
      type: "DateTime",
      description: "The date the payment was initiated (UTC)."
    },
    {
      name: "TransactionId",
      type: "string?",
      description: "Reference number for payments that are not by check."
    },
    {
      name: "CheckNumber",
      type: "string",
      description: "Reference number for check-based payments."
    },
    {
      name: "BankAccountNumber",
      type: "string",
      description: "Masked or partial bank account identifier."
    },
    {
      name: "DepositDateUTC",
      type: "DateTime",
      description: "The date funds were deposited (UTC)."
    },
    {
      name: "Amount",
      type: "decimal",
      description: "The total gross amount of the check/transaction."
    },
    {
      name: "AppliedAmount",
      type: "decimal",
      description: "The specific portion of the amount applied to the invoice."
    },
    {
      name: "IsSplitPayment",
      type: "bool",
      description: "Indicates if the payment is distributed across multiple accounts."
    },
    {
      name: "CheckMemo",
      type: "string",
      description: "Internal notes or memo line from the payment instrument."
    },
    {
      name: "IsActiveSubscription",
      type: "bool",
      description: 'Flag "false" to trigger subscription cancellation upon processing.'
    },
    {
      name: "CustomerNote",
      type: "string?",
      description: "Optional comments or instructions provided by the customer."
    },
    {
      name: "CreateOrder",
      type: "bool",
      description: "If true, system will generate a new order for this payment."
    },
    {
      name: "UpdateSubscriptionAmount",
      type: "bool",
      description: "If true, adjusts the recurring subscription price."
    }
  ];
  const tableRows = columns2.map(({ name, type, description, values }) => {
    const valuesList = values ? `<ul class="values-list">${values.map((v) => `<li><code>${v.code}</code> &mdash; ${v.label}</li>`).join("")}</ul>` : "";
    return `
      <tr>
        <td><code>${name}</code></td>
        <td><span class="type ${type.endsWith("?") ? "optional" : ""}">${type}</span></td>
        <td>${description}${valuesList}</td>
      </tr>`;
  }).join("");
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Payment Import Template — Column Reference</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.6;
      color: #1a1a2e;
      background: #f5f7fa;
      padding: 40px 24px;
    }
    .container { max-width: 900px; margin: 0 auto; }
    header {
      background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
      color: #fff;
      border-radius: 12px;
      padding: 32px 36px;
      margin-bottom: 32px;
    }
    header h1 { font-size: 22px; font-weight: 700; letter-spacing: -0.3px; }
    header p { margin-top: 8px; opacity: 0.75; font-size: 13px; }
    section {
      background: #fff;
      border-radius: 10px;
      box-shadow: 0 1px 4px rgba(0,0,0,.08);
      margin-bottom: 24px;
      overflow: hidden;
    }
    section h2 {
      font-size: 13px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: .6px;
      color: #6b7280;
      padding: 16px 24px;
      border-bottom: 1px solid #f0f0f5;
    }
    table { width: 100%; border-collapse: collapse; }
    thead th {
      background: #fafafa;
      text-align: left;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: .5px;
      color: #9ca3af;
      padding: 10px 20px;
      border-bottom: 1px solid #f0f0f5;
    }
    tbody tr { border-bottom: 1px solid #f7f7fb; }
    tbody tr:last-child { border-bottom: none; }
    tbody tr:hover { background: #fafbff; }
    tbody td { padding: 11px 20px; vertical-align: top; }
    tbody td:first-child { width: 220px; }
    tbody td:nth-child(2) { width: 110px; }
    code {
      font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', monospace;
      font-size: 12px;
      background: #f0f2f5;
      padding: 2px 6px;
      border-radius: 4px;
      color: #374151;
    }
    .type {
      display: inline-block;
      font-size: 11px;
      font-weight: 600;
      padding: 2px 8px;
      border-radius: 20px;
      background: #ede9fe;
      color: #5b21b6;
    }
    .type.optional {
      background: #fef3c7;
      color: #92400e;
    }
    .notes { padding: 20px 24px; }
    .notes ul { padding-left: 20px; }
    .notes li { margin-bottom: 8px; font-size: 13px; color: #4b5563; }
    .notes li code { font-size: 12px; }
    .values-list {
      margin-top: 6px;
      padding-left: 16px;
      list-style: disc;
      color: #6b7280;
      font-size: 12px;
    }
    .values-list code { font-size: 11px; }
    footer {
      text-align: center;
      font-size: 11px;
      color: #9ca3af;
      margin-top: 16px;
    }
  </style>
</head>
<body>
  <div class="container">
    <header>
      <h1>Payment Import Template &mdash; Column Reference</h1>
      <p>Describes every column accepted by the Payment Import CSV template.</p>
    </header>

    <section>
      <h2>Columns</h2>
      <table>
        <thead>
          <tr>
            <th>Column Name</th>
            <th>Data Type</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>${tableRows}
        </tbody>
      </table>
    </section>

    <section>
      <h2>Notes</h2>
      <div class="notes">
        <ul>
          <li>Fields with a <span class="type optional">type?</span> badge are <strong>optional</strong> and may be left blank.</li>
          <li><strong>DateTime</strong> values must be in ISO 8601 format: <code>YYYY-MM-DDTHH:mm:ss</code>.</li>
          <li><strong>Enum</strong> values for <code>TargetPaymentType</code> use the <code>PaymentTypeRequest</code> classification.</li>
        </ul>
      </div>
    </section>

    <footer>Generated on ${(/* @__PURE__ */ new Date()).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</footer>
  </div>
</body>
</html>`;
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "payment-import-template-README.html";
  a.click();
  URL.revokeObjectURL(url);
};
const PaymentImportCreateView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(
    PaymentImportCreate.Modal,
    {
      isOpen: true,
      isDismissable: false,
      title: /* @__PURE__ */ jsxs("div", { className: "flex flex-row justify-between text-lg font-semibold mb-4", children: [
        "New Payment Import",
        " ",
        /* @__PURE__ */ jsxs("div", { className: "flex flex-row gap-2", children: [
          /* @__PURE__ */ jsx(Button, { variant: "secondary", onPress: downloadReadme, children: "Download README" }),
          /* @__PURE__ */ jsx(Button, { variant: "primary", onPress: downloadTemplate, children: "Download Template" })
        ] })
      ] }),
      onSuccess: (payload) => navigate(RoutePaths().payments.imports.details(payload.id)),
      onClose: () => navigate(RoutePaths().payments.imports.list())
    }
  );
};
const PaymentImportCreate = reactive.component(
  ({ services, monitors, db, onSuccess, onClose }) => {
    const [form, setForm] = useState({
      payments: [],
      validationErrors: [],
      validationWarnings: []
    });
    const navigate = useNavigate();
    useSuccessReactor(services.paymentImports.parseCSVDataToJSON, (payload) => {
      if (deepEquals(form.payments, payload)) {
        return;
      }
      setForm((prev) => ({
        ...prev,
        payments: payload
      }));
      services.paymentImports.paymentImportOperation(
        "validate-import",
        JSON.stringify({
          name: form?.data.name,
          payments: payload
        })
      );
    });
    useSuccessReactor(services.paymentImports.paymentImportOperation, (payload) => {
      setForm((prev) => ({
        ...prev,
        ...payload
      }));
    });
    useSuccessReactor(services.paymentImports.createPaymentImport, (payload) => {
      navigate(RoutePaths().payments.imports.details(payload.id));
    });
    return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      /* @__PURE__ */ jsx(
        PaymentImportForm,
        {
          onChange: ({ isValid, data }) => {
            setForm((prev) => ({
              ...prev,
              payments: data.attachment ? prev.payments : [],
              validationErrors: data.attachment ? prev.validationErrors : [],
              validationWarnings: data.attachment ? prev.validationWarnings : [],
              isValid,
              data
            }));
            data.attachment && services.paymentImports.parseCSVDataToJSON(data.attachment);
          }
        }
      ),
      form.payments.length > 0 && /* @__PURE__ */ jsx(PaymentImportDetailsTotal, { isValid: form.isValid, payments: form.payments }),
      form.payments.length > 0 && (form.validationErrors.length > 0 || form.validationWarnings.length > 0) && /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2", children: /* @__PURE__ */ jsx(
        PaymentImportErrorsAndWarningsList,
        {
          errors: form.validationErrors,
          warnings: form.validationWarnings
        }
      ) }),
      /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-end gap-2", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col sm:flex-row items-stretch sm:items-center gap-2", children: [
        /* @__PURE__ */ jsx(ErrorListChip, { errors: [...form?.errors || []] }),
        /* @__PURE__ */ jsx(
          ResourceActions,
          {
            onCancel: () => onClose && onClose(),
            onSave: () => services.paymentImports.createPaymentImport({
              name: form?.data.name,
              ignoreWarnings: true,
              payments: form.payments
            }),
            isSaveEnabled: form?.isValid && !monitors.paymentImportOperation.executing,
            isLoading: monitors.createPaymentImport.executing
          }
        )
      ] }) })
    ] }) });
  },
  {
    name: "PaymentImportCreateView",
    monitors: (services) => [
      services.paymentImports.createPaymentImport,
      services.paymentImports.paymentImportOperation
    ]
  }
);
PaymentImportCreate.Modal = withModalController(({ onSuccess, onClose, ...props }) => /* @__PURE__ */ jsx(PaymentImportCreate, { onSuccess, onClose, ...props }));
const PaymentImportDetailHeader = ({ paymentImport }) => {
  return /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-4 w-full px-2 py-1", children: /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-start", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
      /* @__PURE__ */ jsx("h1", { className: "text-2xl font-bold leading-none text-default-900", children: paymentImport.name }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-small text-default-500", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-user-line" }),
        /* @__PURE__ */ jsx("span", { className: "font-medium", children: paymentImport.createdBy })
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "flex flex-col items-end gap-2", children: /* @__PURE__ */ jsxs(Chip, { variant: "tertiary", color: "accent", children: [
      /* @__PURE__ */ jsx("i", { className: "ri-calendar-line ml-1" }),
      parseAndFormatDateTime(paymentImport.createdOnUTC)
    ] }) })
  ] }) });
};
const PaymentImportDetailsTable = ({ paymentImportChildren }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const openErrorModal = (row) => {
    setSelectedRow(row);
    setIsOpen(true);
  };
  const closeModal = () => {
    setIsOpen(false);
    setSelectedRow(null);
  };
  const getRowClass = (row) => {
    if (row.error) return "bg-danger-50";
    if (!row.reconciled) return "bg-warning-50";
    return "";
  };
  const renderCell = useCallback((row, columnKey) => {
    switch (columnKey) {
      case "paymentDateUTC":
      case "depositDateUTC":
        return row[columnKey] ? parseAndFormatDateTime(row[columnKey]) : "";
      case "amount":
      case "appliedAmount":
        return row[columnKey] ? formatCurrencyUSD(row[columnKey]) : "";
      case "isActiveSubscription":
      case "isSplitPayment":
        return /* @__PURE__ */ jsx(Chip, { color: row[columnKey] ? "success" : "default", children: row[columnKey] ? "Yes" : "No" });
      case "correlationId":
        return row[columnKey] ? /* @__PURE__ */ jsx(KeyDisplay, { keyValue: row[columnKey], maxLength: 6, dir: "end" }) : "";
      case "targetPaymentType":
        return TargetPaymentTypeDisplay[row[columnKey]] ?? row[columnKey];
      case "error":
        return row[columnKey] ? /* @__PURE__ */ jsx(Button, { isIconOnly: true, variant: "danger-soft", onPress: () => openErrorModal(row), children: /* @__PURE__ */ jsx("i", { className: "ri-error-warning-line" }) }) : "";
      default:
        return row[columnKey];
    }
  }, []);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      RequestBrokerTable,
      {
        columns: PaymentImportDetailsColumnDefinitions,
        chipScope: "global",
        items: paymentImportChildren,
        renderColumn: (column) => /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, id: column.propertyName, allowsSorting: column?.isSortable, children: column.name }, column.propertyName),
        renderRow: (row) => /* @__PURE__ */ jsx(Table.Row, { id: row.id, children: PaymentImportDetailsColumnDefinitions.map((column) => /* @__PURE__ */ jsx(Table.Cell, { className: getRowClass(row), children: renderCell(row, column.propertyName) }, column.propertyName)) }, row.id)
      }
    ),
    /* @__PURE__ */ jsx(
      Modal.Backdrop,
      {
        isOpen,
        onOpenChange: (open) => {
          if (!open) closeModal();
        },
        children: /* @__PURE__ */ jsx(Modal.Container, { size: "sm", children: /* @__PURE__ */ jsxs(Modal.Dialog, { children: [
          /* @__PURE__ */ jsx(Modal.Header, { children: /* @__PURE__ */ jsx(Modal.Heading, { className: "text-lg font-semibold", children: "Error Details" }) }),
          /* @__PURE__ */ jsx(Modal.Body, { children: selectedRow && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 text-sm", children: [
            /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-3", children: [
              /* @__PURE__ */ jsxs("div", { className: "bg-default-50 rounded-lg p-3", children: [
                /* @__PURE__ */ jsx("p", { className: "text-xs text-default-500", children: "Payor:" }),
                /* @__PURE__ */ jsx("p", { className: "font-medium text-default-900", children: selectedRow.payorName })
              ] }),
              /* @__PURE__ */ jsxs("div", { className: "bg-default-50 rounded-lg p-3", children: [
                /* @__PURE__ */ jsx("p", { className: "text-xs text-default-500", children: "Customer:" }),
                /* @__PURE__ */ jsx("p", { className: "font-medium text-default-900", children: selectedRow.customerName })
              ] }),
              /* @__PURE__ */ jsxs("div", { className: "bg-default-50 rounded-lg p-3", children: [
                /* @__PURE__ */ jsx("p", { className: "text-xs text-default-500", children: "Amount:" }),
                /* @__PURE__ */ jsx("p", { className: "font-semibold text-primary", children: formatCurrencyUSD(selectedRow.amount) })
              ] }),
              /* @__PURE__ */ jsxs("div", { className: "bg-default-50 rounded-lg p-3", children: [
                /* @__PURE__ */ jsx("p", { className: "text-xs text-default-500", children: "Payment Date:" }),
                /* @__PURE__ */ jsx("p", { className: "font-medium text-default-900", children: parseAndFormatDateTime(selectedRow.paymentDateUTC) })
              ] })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "bg-default-50 border border-danger-200 rounded-lg p-3", children: [
              /* @__PURE__ */ jsx("p", { className: "text-xs text-danger-600 mb-1", children: "Error:" }),
              /* @__PURE__ */ jsx("p", { className: "text-danger font-medium", children: selectedRow.error })
            ] })
          ] }) })
        ] }) })
      }
    )
  ] });
};
const PaymentImportTotalHeader = ({ paymentImport }) => {
  const totalMissingAmount = paymentImport.totalAppliedAmount - paymentImport.totalConsolidatedAmount;
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3 w-full px-2 py-1", children: [
    /* @__PURE__ */ jsx("h1", { className: "text-xl font-semibold text-default-900", children: "Totals" }),
    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3", children: [
      /* @__PURE__ */ jsx(Card, { className: "shadow-xs border border-default-200", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-1 p-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 text-default-500 text-xs", children: [
          /* @__PURE__ */ jsx("i", { className: "ri-file-list-line text-base" }),
          "Imported"
        ] }),
        /* @__PURE__ */ jsx("span", { className: "text-lg font-semibold text-default-900", children: paymentImport.count })
      ] }) }),
      /* @__PURE__ */ jsx(Card, { className: "shadow-xs border border-default-200", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-1 p-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 text-default-500 text-xs", children: [
          /* @__PURE__ */ jsx("i", { className: "ri-file-check-line text-base" }),
          "Completed"
        ] }),
        /* @__PURE__ */ jsx("span", { className: "text-lg font-semibold text-default-900", children: paymentImport.consolidatedCount })
      ] }) }),
      /* @__PURE__ */ jsx(Card, { className: "shadow-xs border border-default-200", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-1 p-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 text-default-500 text-xs", children: [
          /* @__PURE__ */ jsx("i", { className: "ri-money-dollar-circle-line text-base" }),
          "Applied"
        ] }),
        /* @__PURE__ */ jsx("span", { className: "text-lg font-semibold text-primary", children: formatCurrencyUSD(paymentImport.totalAppliedAmount) })
      ] }) }),
      /* @__PURE__ */ jsx(Card, { className: "shadow-xs border border-default-200", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-1 p-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 text-default-500 text-xs", children: [
          /* @__PURE__ */ jsx("i", { className: "ri-bar-chart-box-line text-base" }),
          "Completed $"
        ] }),
        /* @__PURE__ */ jsx("span", { className: "text-lg font-semibold text-success", children: formatCurrencyUSD(paymentImport.totalConsolidatedAmount) })
      ] }) }),
      /* @__PURE__ */ jsx(Card, { className: "shadow-xs border border-default-200", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-1 p-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 text-default-500 text-xs", children: [
          /* @__PURE__ */ jsx("i", { className: "ri-bar-chart-box-line text-base" }),
          "Refunded $"
        ] }),
        /* @__PURE__ */ jsx("span", { className: "text-lg font-semibold text-secondary", children: formatCurrencyUSD(paymentImport.totalRefundedAmount) })
      ] }) }),
      /* @__PURE__ */ jsx(Card, { className: "shadow-xs border border-default-200", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-1 p-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 text-default-500 text-xs", children: [
          /* @__PURE__ */ jsx("i", { className: "ri-error-warning-line text-base" }),
          "Errors"
        ] }),
        /* @__PURE__ */ jsx(
          "span",
          {
            className: `text-lg font-semibold ${paymentImport.totalErrors > 0 ? "text-danger" : "text-default-900"}`,
            children: paymentImport.totalErrors
          }
        )
      ] }) })
    ] }),
    /* @__PURE__ */ jsx(Card, { className: "shadow-xs border border-default-200", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-row justify-between items-center p-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-default-500 text-xs", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-error-warning-line text-base" }),
        "Missing Amount"
      ] }),
      /* @__PURE__ */ jsx(
        "span",
        {
          className: `text-lg font-semibold ${totalMissingAmount < 0 ? "text-warning" : totalMissingAmount === 0 ? "text-success" : "text-danger"}`,
          children: formatCurrencyUSD(totalMissingAmount)
        }
      )
    ] }) })
  ] });
};
const PaymentImportDetailsView = reactive.component(
  ({ services, data, monitors, db, paymentImportId }) => {
    const navigate = useNavigate();
    const { paymentImport, paymentImportChildren } = data;
    const handleExecute = useCallback(
      (action, metadata) => {
        services.paymentImports.getPaymentImport(Number(paymentImportId));
        action(metadata, paymentImportId);
      },
      [paymentImportId]
    );
    return /* @__PURE__ */ jsx("div", { className: "flex flex-col w-full h-full items-center gap-2", children: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between w-full mb-4", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
        /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Payments" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { onPress: () => navigate(RoutePaths().payments.imports.list()), children: "Imports" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Details" })
      ] }) }),
      /* @__PURE__ */ jsx(Card, { className: "w-full", children: /* @__PURE__ */ jsx(Card.Content, { children: /* @__PURE__ */ jsx(PaymentImportDetailHeader, { paymentImport }) }) }),
      /* @__PURE__ */ jsx(Card, { className: "w-full", children: /* @__PURE__ */ jsx(Card.Content, { children: /* @__PURE__ */ jsx(PaymentImportTotalHeader, { paymentImport }) }) }),
      /* @__PURE__ */ jsx(Card, { className: "w-full", children: /* @__PURE__ */ jsx(Card.Content, { children: /* @__PURE__ */ jsx(
        RequestBroker,
        {
          actionKey: `${services.paymentImportDetails.queryPaymentImportDetails.action}-${paymentImportId}`,
          action: services.paymentImportDetails.queryPaymentImportDetails,
          sorting: new Sorting(
            [
              {
                value: "reconciled",
                title: "Consolidated"
              }
            ],
            "reconciled",
            "asc"
          ),
          filters: PaymentImportDetailFilterDefinitions,
          staticFilters: {
            handler: (filters2) => {
              filters2.setValue("paymentImportId", Number(paymentImportId));
            }
          },
          isExecuting: monitors.queryPaymentImportDetails.executing,
          onExecute: handleExecute,
          deps: [paymentImportId],
          children: /* @__PURE__ */ jsx(PaymentImportDetailsTable, { paymentImportChildren })
        }
      ) }) })
    ] }) });
  },
  {
    name: "PaymentImportDetailsView",
    sync: (services, { paymentImportId }) => [
      {
        execute: () => {
          return services.paymentImports.getPaymentImport(Number(paymentImportId));
        },
        deps: [paymentImportId]
      }
    ],
    queries: ({ paymentImportId }, services) => [
      {
        name: "paymentImport",
        execute: (db) => db.payment_imports.get(Number(paymentImportId)),
        defaultValue: {},
        deps: [paymentImportId]
      },
      {
        name: "paymentImportChildren",
        execute: (db) => db.withRequestBroker(
          `${services.paymentImportDetails.queryPaymentImportDetails.action}-${paymentImportId}`,
          () => db.payment_import_details.where({ paymentImportId: Number(paymentImportId) }),
          {
            sorting: true
          }
        ),
        deps: [paymentImportId]
      }
    ],
    monitors: (services) => [services.paymentImportDetails.queryPaymentImportDetails]
  }
);
const PaymentsRouter = () => /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(Route, { path: "/imports", element: /* @__PURE__ */ jsx(PaymentImportsView, {}), children: /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(PaymentImportCreateView, {}) }) }),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "/imports/:paymentImportId/details",
      element: /* @__PURE__ */ jsx(WithParams, { element: PaymentImportDetailsView })
    }
  )
] });
const ProfileForm = ({ profile, onChange }) => {
  return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-4", onChange, children: [
    /* @__PURE__ */ jsx(SwitchField, { size: "sm", name: "isDefault", value: profile?.isDefault || false, children: "Default Profile" }),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "name",
        label: "Profile Name",
        value: profile?.name,
        isRequired: true,
        validation: (x) => x.string().max(100).required("Profile Name is required"),
        variant: "faded"
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "referenceId",
        label: "Account #",
        isReadOnly: profile?.id,
        value: profile?.referenceId,
        isRequired: true,
        validation: (x) => x.string().max(100).required("Reference is required"),
        variant: "faded"
      }
    ),
    /* @__PURE__ */ jsx(
      FundingTypeLookup,
      {
        name: "fundingType",
        type: "number",
        label: profile?.id ? "Funding Type (Read-only)" : "Funding Type",
        isDisabled: profile?.id,
        isReadOnly: profile?.id,
        value: profile?.fundingType,
        isRequired: true,
        validation: (x) => x.number().required("Funding Type is required"),
        variant: "faded"
      }
    )
  ] });
};
const CardForm = ({ creditCard, withCCV, onChange }) => {
  const currentYear = (/* @__PURE__ */ new Date()).getFullYear();
  const messages = getCardValidationMessages(currentYear);
  return creditCard && /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-4", onChange, children: [
    /* @__PURE__ */ jsx(
      TextField,
      {
        type: "text",
        startContent: /* @__PURE__ */ jsx("i", { className: "text-lg ri-info-card-line" }),
        name: "nameOnCard",
        label: "Card Name",
        value: creditCard?.nameOnCard,
        isRequired: true,
        validation: (x) => x.string().required(messages.nameOnCard.required),
        variant: "faded"
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        type: "text",
        startContent: /* @__PURE__ */ jsx("i", { className: "text-lg ri-bank-card-2-line" }),
        name: "cardNumber",
        label: creditCard?.maskedCardNumber ? "Card number (Read-only)" : "Card number",
        value: creditCard?.maskedCardNumber,
        isDisabled: creditCard?.maskedCardNumber,
        isReadOnly: creditCard?.maskedCardNumber,
        isRequired: true,
        validation: (x) => x.string().required(messages.cardNumber.required),
        variant: "faded"
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "flex gap-4", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center grow", children: [
        /* @__PURE__ */ jsx(
          TextField,
          {
            type: "number",
            label: "Month",
            max: "12",
            minLength: 0,
            min: 0,
            placeholder: "MM",
            name: "expirationMonth",
            value: creditCard?.expirationMonth,
            validation: (x) => x.string().matches(/^([1-9]|1[0-2])$/, messages.expirationMonth.matches).required(messages.expirationMonth.required),
            variant: "faded"
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "text-lg mx-2 text-default-300", children: "/" }),
        /* @__PURE__ */ jsx(
          TextField,
          {
            max: "3000",
            label: "Year",
            minLength: 0,
            type: "number",
            min: 2024,
            name: "expirationYear",
            value: creditCard?.expirationYear,
            placeholder: "YYYY",
            validation: (x) => x.string().matches(/^\d{2,4}$/, messages.expirationYear.matches).test("is-greater-than-current-year", messages.expirationYear.test, (value) => {
              const inputYear = value.length === 2 ? parseInt("20" + value) : parseInt(value);
              return inputYear >= currentYear;
            }).required(messages.expirationYear.required),
            variant: "faded"
          }
        )
      ] }),
      withCCV && /* @__PURE__ */ jsx(
        TextField,
        {
          max: "999",
          label: "CVV",
          minLength: 0,
          min: 0,
          name: "cardCvv",
          type: "password",
          isReadOnly: creditCard?.maskedCardNumber,
          value: creditCard?.cardCvv,
          isRequired: true,
          validation: (x) => x.string().matches(/^\d{3}$/, messages.cardCvv.matches).required(messages.cardCvv.required),
          variant: "faded",
          className: "w-1/3"
        }
      )
    ] })
  ] });
};
const getCardValidationMessages = (currentYear) => {
  return {
    nameOnCard: {
      required: "Card Name is required"
    },
    cardNumber: {
      matches: "Card Number must be exactly 16 digits",
      required: "Card Number is required"
    },
    expirationMonth: {
      matches: "Expiration month must be between 1 and 12",
      required: "Expiration month is required"
    },
    expirationYear: {
      matches: "Invalid expiration year",
      test: `Year must be ${currentYear} or later`,
      required: "Expiration year is required"
    },
    cardCvv: {
      matches: "CVV must be 3 digits",
      required: "CVV is required"
    }
  };
};
const ACHForm = ({ ach, onChange }) => {
  return ach && /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-4", onChange, children: [
    /* @__PURE__ */ jsx(
      TextField,
      {
        type: "text",
        name: "bankName",
        label: "Bank Name",
        value: ach?.bankName,
        isReadOnly: ach?.id,
        isRequired: true,
        validation: (x) => x.string().required("Bank Name is required"),
        variant: "faded"
      }
    ),
    /* @__PURE__ */ jsx(
      ACHTypeLookup,
      {
        name: "accountType",
        label: "Account Type",
        value: ach?.bankAccountType,
        isDisabled: ach?.id,
        selectionMode: "single",
        isRequired: true,
        validation: (x) => x.number().required("Account Type is required"),
        variant: "faded"
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        type: "text",
        name: "routingNumber",
        label: "Routing Number",
        value: ach?.maskedRoutingNumber,
        isReadOnly: ach?.id,
        isRequired: true,
        validation: (x) => x.string().required("Routing Number is required"),
        variant: "faded"
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        type: "text",
        name: "accountNumber",
        label: "Account Number",
        value: ach?.maskedAccountNumber,
        isReadOnly: ach?.id,
        isRequired: true,
        validation: (x) => x.string().required("Account Number is required"),
        variant: "faded"
      }
    )
  ] });
};
const ContactForm = ({ contact, onChange }) => {
  const [address, setAddress] = useState(contact);
  useEffect(() => {
    setAddress(contact);
  }, [contact]);
  return /* @__PURE__ */ jsx(Uniformly, { onChange, children: /* @__PURE__ */ jsxs(Tabs, { "aria-label": "Billing contact form", children: [
    /* @__PURE__ */ jsx(Tabs.ListContainer, { children: /* @__PURE__ */ jsxs(Tabs.List, { "aria-label": "Billing contact form", children: [
      /* @__PURE__ */ jsxs(Tabs.Tab, { id: "responsibleParty", children: [
        "Billing Contact",
        /* @__PURE__ */ jsx(Tabs.Indicator, {})
      ] }),
      /* @__PURE__ */ jsxs(Tabs.Tab, { id: "billingAddress", children: [
        "Billing Address",
        /* @__PURE__ */ jsx(Tabs.Indicator, {})
      ] })
    ] }) }),
    /* @__PURE__ */ jsx(Tabs.Panel, { id: "responsibleParty", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "firstName",
          label: "First Name",
          value: contact?.firstName,
          isRequired: true,
          validation: (x) => x.string().max(100).required("First Name is required"),
          variant: "faded"
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "lastName",
          label: "Last Name",
          value: contact?.lastName,
          isRequired: true,
          validation: (x) => x.string().max(100).required("Last Name is required"),
          variant: "faded"
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "email",
          label: "Email",
          value: contact?.email,
          validation: (x) => x.string().max(100),
          variant: "faded"
        }
      ),
      /* @__PURE__ */ jsx(
        PhoneField,
        {
          name: "primaryPhone",
          label: "Primary Phone",
          value: contact?.primaryPhone,
          validation: (x) => x.string().max(100),
          variant: "faded",
          endContent: /* @__PURE__ */ jsx(
            SwitchField,
            {
              size: "sm",
              name: "acceptTextPrimary",
              value: contact?.acceptTextPrimary || false,
              children: "SMS"
            }
          )
        }
      ),
      /* @__PURE__ */ jsx(
        PhoneField,
        {
          name: "secondaryPhone",
          label: "Secondary Phone",
          value: contact?.secondaryPhone,
          validation: (x) => x.string().max(100).notRequired(),
          variant: "faded",
          endContent: /* @__PURE__ */ jsx(
            SwitchField,
            {
              size: "sm",
              name: "acceptTextSecondary",
              value: contact?.acceptTextSecondary || false,
              children: "SMS"
            }
          )
        }
      )
    ] }) }),
    /* @__PURE__ */ jsx(Tabs.Panel, { id: "billingAddress", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      /* @__PURE__ */ jsx(
        AddressLookupField,
        {
          startContent: /* @__PURE__ */ jsx("i", { className: "text-lg text-gray-400 ri-map-pin-line" }),
          placeholder: "Search address...",
          onSelect: (addr) => {
            setAddress({
              address1: addr?.address1,
              address2: addr?.address2,
              address3: addr?.address3,
              city: addr?.city,
              state: addr?.state,
              postalCode: addr?.postalCode,
              country: addr?.country
            });
          },
          variant: "faded"
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          label: "Address 1",
          variant: "faded",
          id: "address1",
          name: "address1",
          size: "sm",
          value: address?.address1,
          isRequired: true,
          validation: (x) => x.string().max(100).required(`Address 1 is required`)
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          label: "Address 2",
          variant: "faded",
          id: "address2",
          name: "address2",
          size: "sm",
          value: address?.address2,
          validation: (x) => x.string().max(100).notRequired()
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          label: "Address 3",
          variant: "faded",
          id: "address3",
          name: "address3",
          size: "sm",
          value: address?.address3,
          validation: (x) => x.string().max(100).notRequired()
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          label: "City",
          variant: "faded",
          id: "city",
          name: "city",
          size: "sm",
          value: address?.city,
          isRequired: true,
          validation: (x) => x.string().max(100).required(`City is required`)
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          label: "State",
          variant: "faded",
          id: "state",
          name: "state",
          size: "sm",
          value: address?.state,
          isRequired: true,
          validation: (x) => x.string().max(100).required(`State is required`)
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          label: "Postal code",
          variant: "faded",
          id: "postalCode",
          name: "postalCode",
          size: "sm",
          value: address?.postalCode,
          isRequired: true,
          validation: (x) => x.string().max(100).required(`Postal code is required`)
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          label: "Country",
          variant: "faded",
          id: "country",
          name: "country",
          size: "sm",
          value: address?.country,
          isRequired: true,
          validation: (x) => x.string().max(100).required(`Country is required`)
        }
      )
    ] }) })
  ] }) });
};
const ProfileUpdateLoader = reactive.component(
  ({ services, data, monitors, profileId, onSuccess, onDelete, children }) => {
    const [form, setForm] = useForms();
    useSuccessReactor(services.profiles.updateProfile, (payload) => {
      onSuccess && onSuccess(payload);
    });
    useSuccessReactor(services.profiles.deleteProfile, () => {
      onDelete && onDelete();
    });
    return children({
      form,
      profile: data.profile,
      onChange: (key, value) => setForm(key, value),
      onSave: (data2) => services.profiles.updateProfile(profileId, data2),
      onDelete: () => services.profiles.deleteProfile(profileId),
      unfreezeProfile: () => services.profiles.profileOperation("unfreeze-profile", profileId),
      onFlagRule: (code) => services.profiles.profileOperation(
        "rule-flag",
        JSON.stringify({ ProfileId: profileId, ...code })
      ),
      monitors: {
        updateProfile: monitors.updateProfile.executing,
        deleteProfile: monitors.deleteProfile.executing,
        unfreezeProfile: monitors.profileOperation.executing
      }
    });
  },
  {
    name: "ProfileUpdateLoader",
    sync: (services, { profileId }) => [
      {
        execute: () => services.profiles.getProfile(profileId),
        deps: [profileId]
      }
    ],
    queries: ({ profileId }) => [
      {
        name: "profile",
        execute: (db) => db.profiles.get(profileId),
        defaultValue: {},
        deps: [profileId]
      }
    ],
    monitors: (services) => [
      services.profiles.updateProfile,
      services.profiles.deleteProfile,
      services.profiles.profileOperation
    ]
  }
);
const ProfileUpdateController = ({ profileId, onSuccess, onDelete, onCancel }) => {
  const [fundingTypeSelected, setSelected] = useState(FundingType.CreditCard);
  return /* @__PURE__ */ jsx(ProfileUpdateLoader, { profileId, onSuccess, onDelete, children: ({ form, profile, onChange, onSave, onDelete: onDelete2, unfreezeProfile, onFlagRule, monitors }) => /* @__PURE__ */ jsxs(ScrollShadow, { offset: 1, hideScrollBar: true, children: [
    /* @__PURE__ */ jsxs(Tabs, { "aria-label": "Profile details form", children: [
      /* @__PURE__ */ jsx(Tabs.ListContainer, { children: /* @__PURE__ */ jsxs(Tabs.List, { "aria-label": "Profile details form", className: "overflow-x-auto", children: [
        /* @__PURE__ */ jsxs(Tabs.Tab, { id: "generalInfo", children: [
          "General",
          /* @__PURE__ */ jsx(Tabs.Indicator, {})
        ] }),
        fundingTypeSelected !== FundingType.Cash && /* @__PURE__ */ jsxs(Tabs.Tab, { id: "paymentInfo", children: [
          "Payment",
          /* @__PURE__ */ jsx(Tabs.Indicator, {})
        ] }),
        /* @__PURE__ */ jsxs(Tabs.Tab, { id: "contactInfo", children: [
          "Contact",
          /* @__PURE__ */ jsx(Tabs.Indicator, {})
        ] }),
        fundingTypeSelected !== FundingType.Cash && false
      ] }) }),
      /* @__PURE__ */ jsx(Tabs.Panel, { id: "generalInfo", children: /* @__PURE__ */ jsx(
        ProfileForm,
        {
          profile,
          onChange: (profileForm) => {
            setSelected(profileForm.data.fundingType);
            onChange("profile", profileForm);
          }
        }
      ) }),
      fundingTypeSelected !== FundingType.Cash && /* @__PURE__ */ jsxs(Tabs.Panel, { id: "paymentInfo", children: [
        fundingTypeSelected === FundingType.CreditCard && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("h2", { className: "font-semibold ms-1 mb-1 text-lg md:text-xl lg:text-2xl", children: "Credit Card" }),
          /* @__PURE__ */ jsx(
            CardForm,
            {
              creditCard: profile?.creditCardProfile,
              onChange: (creditCardProfile) => onChange("creditCardProfile", creditCardProfile)
            }
          )
        ] }),
        fundingTypeSelected === FundingType.ACH && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("h2", { className: "font-semibold ms-1 mb-1 text-lg md:text-xl lg:text-2xl", children: "ACH" }),
          /* @__PURE__ */ jsx(
            ACHForm,
            {
              ach: profile?.bankProfile,
              onChange: (bankProfile) => onChange("bankProfile", bankProfile)
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsx(Tabs.Panel, { id: "contactInfo", children: /* @__PURE__ */ jsx(
        ContactForm,
        {
          contact: profile.contact,
          onChange: (contact) => onChange("contact", contact)
        }
      ) }),
      fundingTypeSelected !== FundingType.Cash && false
    ] }),
    /* @__PURE__ */ jsx(
      ResourceActions,
      {
        isSaveEnabled: form.isValid,
        onDelete: onDelete2,
        onCancel: () => onCancel && onCancel(),
        onSave: () => {
          onSave({
            id: profileId,
            ...form.forms.profile.data,
            creditCardProfile: form.forms.creditCardProfile?.data,
            bankProfile: form.forms.bankProfile?.data,
            contact: form.forms.contact.data
          });
        },
        isLoading: monitors.deleteProfile || monitors.updateProfile,
        children: /* @__PURE__ */ jsx("div", { className: "flex justify-end items-center p-1", children: /* @__PURE__ */ jsx(ErrorListChip, { errors: form.errors }) })
      }
    )
  ] }) });
};
ProfileUpdateController.Drawer = withDrawerController(ProfileUpdateController);
ProfileUpdateController.Modal = withModalController(ProfileUpdateController);
const ProfileCreateLoader = reactive.component(
  ({ services, monitors, onSuccess, children }) => {
    const [form, setForm] = useForms();
    useSuccessReactor(services.profiles.createProfile, (payload) => {
      onSuccess && onSuccess(payload);
    });
    return children({
      form,
      onChange: (key, value) => setForm(key, value),
      onSave: (data) => services.profiles.createProfile(data),
      monitors: {
        createProfile: monitors.createProfile.executing
      }
    });
  },
  {
    name: "ProfileCreateLoader",
    monitors: (services) => [services.profiles.createProfile]
  }
);
const ProfileCreateController = reactive.component(
  ({ services, monitors, onSuccess, onClose }) => {
    const [selectedFundingType, setSelectedFundingType] = useState(FundingType.CreditCard);
    useSuccessReactor(services.profiles.createProfile, (payload) => {
      onSuccess && onSuccess(payload);
    });
    return /* @__PURE__ */ jsx(ProfileCreateLoader, { onSuccess, children: ({ form, onChange, onSave, monitors: monitors2 }) => /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs(ScrollShadow, { offset: 1, hideScrollBar: true, className: "flex flex-col p-4 gap-4", children: [
      /* @__PURE__ */ jsxs(Tabs, { "aria-label": "Profile details form", children: [
        /* @__PURE__ */ jsx(Tabs.ListContainer, { children: /* @__PURE__ */ jsxs(Tabs.List, { "aria-label": "Profile details form", className: "overflow-x-auto", children: [
          /* @__PURE__ */ jsxs(Tabs.Tab, { id: "generalInfo", children: [
            "General",
            /* @__PURE__ */ jsx(Tabs.Indicator, {})
          ] }),
          /* @__PURE__ */ jsxs(Tabs.Tab, { id: "paymentInfo", children: [
            "Payment",
            /* @__PURE__ */ jsx(Tabs.Indicator, {})
          ] }),
          /* @__PURE__ */ jsxs(Tabs.Tab, { id: "contactInfo", children: [
            "Contact",
            /* @__PURE__ */ jsx(Tabs.Indicator, {})
          ] })
        ] }) }),
        /* @__PURE__ */ jsx(Tabs.Panel, { id: "generalInfo", children: /* @__PURE__ */ jsx(
          ProfileForm,
          {
            profile: { fundingType: selectedFundingType },
            onChange: (profile) => {
              setSelectedFundingType(profile.data.fundingType);
              onChange("profile", profile);
            }
          }
        ) }),
        /* @__PURE__ */ jsxs(Tabs.Panel, { id: "paymentInfo", children: [
          selectedFundingType === FundingType.CreditCard && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("div", { children: "Credit Card" }),
            /* @__PURE__ */ jsx(
              CardForm,
              {
                onChange: (creditCardProfile) => onChange("creditCardProfile", creditCardProfile)
              }
            )
          ] }),
          selectedFundingType === FundingType.ACH && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("div", { children: "ACH" }),
            /* @__PURE__ */ jsx(ACHForm, { onChange: (bankProfile) => onChange("bankProfile", bankProfile) })
          ] })
        ] }),
        /* @__PURE__ */ jsx(Tabs.Panel, { id: "contactInfo", children: /* @__PURE__ */ jsx(ContactForm, { onChange: (contact) => onChange("contact", contact) }) })
      ] }),
      /* @__PURE__ */ jsx(
        ResourceActions,
        {
          onCancel: onClose,
          onSave: () => {
            services.profiles.createProfile({
              ...form.forms.profile.data,
              creditCardProfile: form.forms.creditCardProfile?.data,
              bankProfile: form.forms.bankProfile?.data,
              contact: form.forms.contact.data
            });
          },
          isSaveEnabled: form.isValid,
          isLoading: monitors2.createProfile.executing,
          children: /* @__PURE__ */ jsx("div", { className: "flex justify-end items-center p-1", children: /* @__PURE__ */ jsx(ErrorListChip, { errors: form.errors }) })
        }
      )
    ] }) }) });
  },
  {
    name: "ProfileCreateController",
    monitors: (services) => [services.profiles.createProfile]
  }
);
ProfileCreateController.Drawer = withDrawerController(ProfileCreateController);
ProfileCreateController.Modal = withModalController(ProfileCreateController);
const ProfileCreateView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(
    ProfileCreateController.Modal,
    {
      headerContent: () => /* @__PURE__ */ jsx("h1", { children: "Create Profile" }),
      isOpen: true,
      isDismissable: false,
      onClose: () => navigate(RoutePaths().profiles.list()),
      onSuccess: () => navigate(RoutePaths().profiles.list()),
      onCancel: () => navigate(RoutePaths().profiles.list())
    }
  );
};
const ProfileUpdateView = ({ profileId }) => {
  profileId = Number(profileId);
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(
    ProfileUpdateController.Modal,
    {
      title: "Update Profile",
      profileId,
      isOpen: true,
      isDismissable: false,
      onClose: () => navigate(RoutePaths().profiles.list()),
      onSuccess: () => navigate(RoutePaths().profiles.list()),
      onDelete: () => navigate(RoutePaths().profiles.list()),
      onCancel: () => navigate(RoutePaths().profiles.list())
    }
  );
};
reactive.component(
  ({ services, data, monitors, children }) => {
    return children(data.profiles, monitors.getProfiles, services.profiles.getProfiles);
  },
  {
    name: "ProfilesByReferenceLoader",
    sync: (services, { referenceId }) => [
      {
        execute: () => {
          console.log("referenceId", referenceId);
          services.profiles.getProfiles(referenceId);
        },
        deps: [referenceId]
      }
    ],
    queries: ({ referenceId }) => [
      {
        name: "profiles",
        execute: (db) => db.profiles.filter((x) => x.referenceId === referenceId).toArray(),
        deps: [referenceId]
      }
    ],
    monitors: (services) => [services.profiles.getProfiles]
  }
);
const columns$2 = [
  { propertyName: "fundingType", name: "" },
  { propertyName: "name", name: "Name", isSortable: true },
  { propertyName: "referenceId", name: "Reference ID" },
  { propertyName: "isDefault", name: "Is Default" },
  { propertyName: "isFrozen", name: "Is Frozen" }
];
const ProfilesList = ({ profiles = [], onSelect }) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "fundingType":
        return /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
          /* @__PURE__ */ jsx(Tooltip.Trigger, { children: FundingTypeIcon[cellValue] }),
          /* @__PURE__ */ jsx(Tooltip.Content, { children: FundingTypeDisplay[cellValue] })
        ] });
      case "isDefault":
        return cellValue && /* @__PURE__ */ jsx(RiCheckboxCircleFill, { className: "text-success", size: 16 });
      case "isFrozen":
        return cellValue && /* @__PURE__ */ jsx(RiTempColdFill, { className: "text-warning", size: 16 });
      default:
        return cellValue;
    }
  }, []);
  return /* @__PURE__ */ jsx(
    RequestBrokerTable,
    {
      columns: columns$2,
      items: profiles,
      renderColumn: (column) => /* @__PURE__ */ jsx(
        Table.Column,
        {
          isRowHeader: true,
          id: column.propertyName,
          width: column.propertyName === "fundingType" ? "2px" : "auto",
          align: column.propertyName === "actions" ? "center" : "start",
          allowsSorting: column?.isSortable,
          children: column.name
        },
        column.propertyName
      ),
      renderRow: (row) => /* @__PURE__ */ jsx(Table.Row, { id: row.id, onAction: () => onSelect(row), className: "cursor-pointer", children: columns$2.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(row, column.propertyName) }, column.propertyName)) }, row.id)
    }
  );
};
const ProfilesRequestBrokerLoader = reactive.component(
  ({ services, data, monitors, children }) => {
    return children(data.profiles, monitors.queryProfiles, services.profiles.queryProfiles);
  },
  {
    name: "ProfilesListLoader",
    queries: (_, services) => [
      {
        name: "profiles",
        execute: (db) => db.withRequestBroker(services.profiles.queryProfiles.action, () => db.profiles, {
          sorting: true
        }),
        deps: []
      }
    ],
    monitors: (services) => [services.profiles.queryProfiles]
  }
);
const ProfileFilterDefinitions = new Filters([
  new FilterInput({
    name: "name",
    title: "Name"
  }),
  new FilterInput({
    name: "referenceId",
    title: "Reference ID",
    options: {
      searchType: "equals"
    },
    render: {
      hideSearchType: true
    }
  }),
  new FilterToggle({
    name: "isDefault",
    title: "Default Profiles"
  }),
  new FilterToggle({
    name: "isFrozen",
    title: "Frozen Profiles"
  })
]);
const ProfilesListRequestBrokerController = ({ onSelect }) => {
  const handleExecute = useCallback((action, metadata) => {
    action(metadata);
  }, []);
  return /* @__PURE__ */ jsx(ProfilesRequestBrokerLoader, { children: (profiles, monitor, action) => {
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: "queryProfiles",
        action,
        filters: ProfileFilterDefinitions,
        isExecuting: monitor.executing,
        onExecute: handleExecute,
        children: /* @__PURE__ */ jsx(ProfilesList, { profiles, onSelect: (item) => onSelect && onSelect(item) })
      }
    );
  } });
};
const ProfilesListView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      ProfilesListRequestBrokerController,
      {
        onSelect: (item) => navigate(RoutePaths().profiles.details(item.id))
      }
    ),
    /* @__PURE__ */ jsx(Outlet, {})
  ] });
};
const columns$1 = [
  { propertyName: "fundingType", name: "" },
  { propertyName: "name", name: "Name", isSortable: true },
  { propertyName: "referenceId", name: "Reference ID" },
  { propertyName: "isDefault", name: "Default" },
  { propertyName: "isFrozen", name: "Frozen" },
  { propertyName: "actions", name: "", align: "end" }
];
const dash = /* @__PURE__ */ jsx("span", { className: "text-[var(--fg-3)]", children: "—" });
reactive.component(
  ({ services, data, monitors, db }) => {
    const navigate = useNavigate();
    const profiles = data.profiles ?? [];
    const handleExecute = useCallback((action, metadata) => action(metadata), []);
    const openDetails = (id) => navigate(RoutePaths().profiles.details(id));
    const openCreate = () => navigate(RoutePaths().profiles.create());
    const remove = (id) => services.profiles.deleteProfile(id);
    const renderCell = (row, key) => {
      switch (key) {
        case "fundingType":
          return /* @__PURE__ */ jsx("span", { title: FundingTypeDisplay[row.fundingType], children: FundingTypeIcon[row.fundingType] });
        case "isDefault":
          return row.isDefault ? /* @__PURE__ */ jsx(RiCheckboxCircleFill, { className: "text-success", size: 16 }) : null;
        case "isFrozen":
          return row.isFrozen ? /* @__PURE__ */ jsx(RiTempColdFill, { className: "text-warning", size: 16 }) : null;
        case "actions":
          return /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-end gap-1.5", children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                isIconOnly: true,
                size: "sm",
                variant: "light",
                "aria-label": "View details",
                onPress: () => openDetails(row.id),
                className: "text-[var(--fg-3)] hover:text-[var(--foreground)]",
                children: /* @__PURE__ */ jsx("i", { className: "ri-arrow-right-s-line" })
              }
            ),
            /* @__PURE__ */ jsx(
              DeleteButton,
              {
                isIconOnly: true,
                size: "sm",
                onPress: () => remove(row.id),
                isLoading: monitors.deleteProfile.executing
              }
            )
          ] });
        default:
          return row[key] ?? dash;
      }
    };
    const defaults = profiles.filter((p) => p.isDefault).length;
    const frozen = profiles.filter((p) => p.isFrozen).length;
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs("div", { className: "mb-3.5 flex items-end justify-between", children: [
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("h1", { className: "m-0 text-xl font-semibold tracking-tight text-[var(--foreground)]", children: "Profiles" }),
          /* @__PURE__ */ jsxs("p", { className: "mt-0.5 text-sm text-[var(--muted)]", children: [
            profiles.length,
            " ",
            profiles.length === 1 ? "profile" : "profiles",
            " · ",
            defaults,
            " default · ",
            frozen,
            " frozen"
          ] })
        ] }),
        /* @__PURE__ */ jsx(NewButton, { label: "Add profile", onPress: openCreate })
      ] }),
      /* @__PURE__ */ jsx(
        RequestBroker,
        {
          actionKey: "queryProfiles",
          action: services.profiles.queryProfiles,
          filters: ProfileFilterDefinitions,
          paging: new Paging(50),
          isExecuting: monitors.queryProfiles.executing,
          skipInitial: () => db.profiles.toCollection().count(),
          onExecute: handleExecute,
          children: /* @__PURE__ */ jsx(
            RequestBrokerTable,
            {
              "aria-label": "Profiles list table",
              columns: columns$1,
              items: profiles,
              renderColumn: (column) => /* @__PURE__ */ jsx(
                Table.Column,
                {
                  id: column.propertyName,
                  align: column.align ?? "start",
                  allowsSorting: column.isSortable,
                  children: column.name
                },
                column.propertyName
              ),
              renderRow: (row) => /* @__PURE__ */ jsx(
                Table.Row,
                {
                  id: row.id,
                  className: "cursor-pointer",
                  onAction: () => openDetails(row.id),
                  children: columns$1.map((column) => /* @__PURE__ */ jsx(Table.Cell, { align: column.align ?? "start", children: renderCell(row, column.propertyName) }, column.propertyName))
                },
                row.id
              )
            }
          )
        }
      )
    ] });
  },
  {
    name: "ProfilesReactiveController",
    queries: (_, services) => [
      {
        name: "profiles",
        execute: (db) => db.withRequestBroker(services.profiles.queryProfiles.action, () => db.profiles, {
          sorting: true
        }),
        deps: []
      }
    ],
    monitors: (services) => [services.profiles.queryProfiles, services.profiles.deleteProfile]
  }
);
const ProfilesRouter = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsxs(Route, { path: "/", element: /* @__PURE__ */ jsx(ProfilesListView, {}), children: [
  /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(ProfileCreateView, {}) }),
  /* @__PURE__ */ jsx(Route, { path: ":profileId/details", element: /* @__PURE__ */ jsx(WithParams, { element: ProfileUpdateView }) })
] }) });
const columns = [
  { propertyName: "code", name: "Code", isSortable: true },
  { propertyName: "merchantType", name: "Merchant Type", isSortable: true },
  { propertyName: "resultType", name: "Result Type", isSortable: true },
  { propertyName: "responseCodeType", name: "Response Code Type", isSortable: true },
  { propertyName: "shortDescription", name: "Short Description", isSortable: true },
  { propertyName: "createdOnUtc", name: "Created On", isSortable: true },
  { propertyName: "freezePayments", name: "Freeze Payments", isSortable: true }
];
const ResponseCodesList = ({ responseCodes = [], onSelect }) => {
  const renderCell = useCallback((row, columnKey) => {
    const cellValue = row[columnKey];
    switch (columnKey) {
      case "code":
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
          /* @__PURE__ */ jsx("span", { className: "font-semibold me-2", children: cellValue }),
          /* @__PURE__ */ jsx(CopyToClipboard, { text: cellValue })
        ] });
      case "merchantType":
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("i", { className: MerchantTypeIcon[cellValue] }),
          /* @__PURE__ */ jsx("p", { children: MerchantTypeDisplay[cellValue] })
        ] });
      case "responseCodeType":
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("div", { className: `${ResponseCodeTypeColor[cellValue]} rounded-md`, children: /* @__PURE__ */ jsx("i", { className: ResponseCodeTypeIcon[cellValue] }) }),
          /* @__PURE__ */ jsx("p", { children: ResponseCodeTypeDisplay[cellValue] })
        ] });
      case "resultType":
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("div", { className: `${TransactionResultTypeColor[cellValue]} rounded-md`, children: /* @__PURE__ */ jsx("i", { className: TransactionResultTypeIcon[cellValue] }) }),
          /* @__PURE__ */ jsx("p", { children: TransactionResultTypeDisplay[cellValue] })
        ] });
      case "freezePayments":
        return /* @__PURE__ */ jsx("i", { className: cellValue ? "ri-temp-cold-fill" : "" });
      case "createdOnUtc":
        return parseAndFormatDate(cellValue);
      case "shortDescription":
        return /* @__PURE__ */ jsx("p", { className: "text-wrap truncate", children: cellValue });
      default:
        return cellValue;
    }
  }, []);
  return /* @__PURE__ */ jsx(
    RequestBrokerTable,
    {
      columns,
      items: responseCodes,
      renderColumn: (column) => /* @__PURE__ */ jsx(
        Table.Column,
        {
          isRowHeader: true,
          id: column.propertyName,
          align: column.propertyName === "actions" ? "center" : "start",
          allowsSorting: column?.isSortable,
          children: column.name
        },
        column.propertyName
      ),
      renderRow: (row) => /* @__PURE__ */ jsx(Table.Row, { id: row.id, onAction: () => onSelect(row), className: "cursor-pointer", children: columns.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(row, column.propertyName) }, column.propertyName)) }, row.id)
    }
  );
};
const ResponseCodeFilterDefinitions = new Filters([
  new FilterInput({
    name: "Name",
    title: "Name"
  }),
  new FilterSelect({
    name: "MerchantType",
    title: "Merchant Type",
    loader: () => Promise.resolve(MerchantTypeOptions),
    render: {
      inputType: "integer"
    }
  }),
  new FilterSelect({
    name: "ResponseCodeType",
    title: "Response Code Type",
    loader: () => Promise.resolve(ResponseCodeTypeOptions),
    render: {
      inputType: "integer"
    }
  }),
  new FilterInput({
    name: "Code",
    title: "Code"
  }),
  new FilterInput({
    name: "ShortDescription",
    title: "Short Description"
  }),
  new FilterInput({
    name: "LongDescription",
    title: "Long Description"
  }),
  new FilterInput({
    name: "ResolutionDescription",
    title: "Resolution Description"
  }),
  new FilterDateRange({
    name: "CreatedOnUTC",
    title: "Created On"
  })
]);
const ResponseCodesListController = reactive.component(
  ({ onSelect }) => {
    return /* @__PURE__ */ jsx(ResponseCodesListLoader, { children: (handleExecute, responseCodes, monitors, services) => {
      return /* @__PURE__ */ jsx(
        RequestBroker,
        {
          actionKey: services.responseCodes.queryResponseCodes.action,
          action: services.responseCodes.queryResponseCodes,
          filters: ResponseCodeFilterDefinitions,
          isExecuting: monitors.queryResponseCodes.executing,
          onExecute: handleExecute,
          children: /* @__PURE__ */ jsx(
            ResponseCodesList,
            {
              responseCodes,
              isLoading: monitors.queryResponseCodes.executing,
              onSelect: (item) => onSelect && onSelect(item)
            }
          )
        }
      );
    } });
  },
  { name: "ResponseCodesListController" }
);
const ResponseCodesListLoader = reactive.component(
  ({ data, services, monitors, children }) => {
    const { responseCodes } = data;
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return children(handleExecute, responseCodes, monitors, services);
  },
  {
    name: "ResponseCodesListLoader",
    queries: (_, services) => [
      {
        name: "responseCodes",
        execute: (db) => db.withRequestBroker(
          services.responseCodes.queryResponseCodes.action,
          () => db.responseCodes,
          {
            sorting: true,
            paging: false
          }
        )
      }
    ],
    monitors: (services) => [services.responseCodes.queryResponseCodes]
  }
);
const ResponseCodesListView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      ResponseCodesListController,
      {
        onSelect: (item) => navigate(RoutePaths().responseCodes.update(item.id))
      }
    ),
    /* @__PURE__ */ jsx(Outlet, {})
  ] });
};
const ResponseCodeForm = ({ responseCode = {}, onChange }) => {
  return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-4", onChange, children: [
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "code",
        label: "Code",
        placeholder: "Enter response code",
        value: responseCode.code,
        validation: (x) => x.string().max(50).required("Code is required"),
        isRequired: true,
        variant: "secondary"
      }
    ),
    /* @__PURE__ */ jsx(
      MerchantTypeLookup,
      {
        name: "merchantType",
        type: "number",
        label: "Merchant Type",
        placeholder: "Select merchant type",
        value: responseCode.merchantType,
        validation: (x) => x.number().required("Merchant Type is required"),
        isRequired: true,
        variant: "secondary"
      }
    ),
    /* @__PURE__ */ jsx(
      ResponseCodeTypesLookup,
      {
        name: "responseCodeType",
        label: "Response Code Type",
        placeholder: "Select response code type",
        value: responseCode.responseCodeType,
        validation: (x) => x.number().required("Response Code Type is required"),
        isRequired: true,
        variant: "secondary"
      }
    ),
    /* @__PURE__ */ jsx(
      TransactionResultTypeLookup,
      {
        name: "resultType",
        label: "Result Type",
        placeholder: "Select result type",
        value: responseCode.resultType,
        validation: (x) => x.number().required("Result Type is required"),
        isRequired: true,
        variant: "secondary"
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "shortDescription",
        label: "Short Description",
        placeholder: "Enter short description",
        value: responseCode.shortDescription,
        validation: (x) => x.string().max(250),
        variant: "secondary"
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "longDescription",
        label: "Long Description",
        placeholder: "Enter long description",
        value: responseCode.longDescription,
        validation: (x) => x.string().max(500),
        variant: "secondary"
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "resolutionDescription",
        label: "Resolution Description",
        placeholder: "Enter resolution description",
        value: responseCode.resolutionDescription,
        validation: (x) => x.string().max(500),
        variant: "secondary"
      }
    ),
    /* @__PURE__ */ jsx(SwitchField, { size: "sm", name: "freezePayments", value: responseCode.freezePayments || false, children: "Freeze Payments" })
  ] });
};
const ResponseCodeCreateController = reactive.component(
  ({ services, monitors, onSuccess, onClose }) => {
    const [form, setForm] = useState({});
    useSuccessReactor(services.responseCodes.createResponseCode, (payload) => {
      onSuccess && onSuccess(payload);
    });
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(ResponseCodeForm, { onChange: setForm }),
      /* @__PURE__ */ jsx(
        ResourceActions,
        {
          onCancel: onClose,
          onSave: () => services.responseCodes.createResponseCode(form.data),
          isSaveEnabled: form.isValid,
          isLoading: monitors.createResponseCode.executing
        }
      )
    ] });
  },
  {
    name: "ResponseCodeCreateController",
    monitors: (services) => [services.responseCodes.createResponseCode]
  }
);
ResponseCodeCreateController.Modal = withModalController(({ onSuccess, onClose, ...props }) => /* @__PURE__ */ jsx(ResponseCodeCreateController, { onSuccess, onClose, ...props }));
const ResponseCodeCreateView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(
    ResponseCodeCreateController.Modal,
    {
      title: "New Response Code",
      isOpen: true,
      isDismissable: false,
      onSuccess: () => navigate(RoutePaths().responseCodes.list()),
      onClose: () => navigate(RoutePaths().responseCodes.list())
    }
  );
};
const ResponseCodeUpdateController = reactive.component(
  ({ responseCodeId, services, monitors, data, onSuccess, onClose }) => {
    const [form, setForm] = useState({});
    const { responseCode } = data;
    const { data: updatedResponseCode } = form;
    useSuccessReactor(services.responseCodes.updateResponseCode, (payload) => {
      onSuccess && onSuccess(payload);
    });
    useSuccessReactor(services.responseCodes.deleteResponseCode, () => {
      onClose && onClose();
    });
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(ResponseCodeForm, { responseCode, onChange: setForm }),
      /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-end gap-x-2 mt-2", children: /* @__PURE__ */ jsx(
        ResourceActions,
        {
          onDelete: () => services.responseCodes.deleteResponseCode(responseCodeId),
          onSave: () => services.responseCodes.updateResponseCode(responseCodeId, {
            ...updatedResponseCode,
            createdOnUtc: responseCode.createdOnUtc,
            id: responseCodeId
          }),
          onCancel: onClose,
          isSaveEnabled: form.isValid,
          isLoading: monitors.deleteResponseCode.executing || monitors.updateResponseCode.executing
        }
      ) })
    ] });
  },
  {
    name: "ResponseCodeUpdateController",
    sync: (services, { responseCodeId }) => [
      {
        execute: () => services.responseCodes.getResponseCode(Number(responseCodeId)),
        deps: [responseCodeId]
      }
    ],
    queries: ({ responseCodeId }) => [
      {
        name: "responseCode",
        execute: (db) => db.responseCodes.get(Number(responseCodeId)),
        defaultValue: {},
        deps: [responseCodeId]
      }
    ],
    monitors: (services) => [
      services.responseCodes.updateResponseCode,
      services.responseCodes.deleteResponseCode,
      services.responseCodes.getResponseCode
    ]
  }
);
ResponseCodeUpdateController.Modal = withModalController(({ onSuccess, onClose, ...props }) => /* @__PURE__ */ jsx(ResponseCodeUpdateController, { onSuccess, onClose, ...props }));
const ResponseCodeUpdateView = ({ responseCodeId }) => {
  const navigate = useNavigate();
  responseCodeId = Number(responseCodeId);
  return /* @__PURE__ */ jsx(
    ResponseCodeUpdateController.Modal,
    {
      title: "Update Response Code",
      isOpen: true,
      isDismissable: false,
      responseCodeId,
      onClose: () => navigate(RoutePaths().responseCodes.list()),
      onSuccess: () => navigate(RoutePaths().responseCodes.list())
    }
  );
};
const ResponseCodesRouter = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsxs(Route, { path: "/", element: /* @__PURE__ */ jsx(ResponseCodesListView, {}), children: [
  /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(ResponseCodeCreateView, {}) }),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: ":responseCodeId/update",
      element: /* @__PURE__ */ jsx(WithParams, { element: ResponseCodeUpdateView })
    }
  )
] }) });
const TransactionsRouter = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsx(Route, { path: "view", element: /* @__PURE__ */ jsx(TransactionsListView, {}), children: /* @__PURE__ */ jsx(
    Route,
    {
      path: ":transactionId/details",
      element: /* @__PURE__ */ jsx(
        WithRouteParams,
        {
          element: TransactionDetailsView,
          isModal: true,
          onSuccess: () => navigate(RoutePaths().transactions.list()),
          onClose: () => navigate(RoutePaths().transactions.list())
        }
      )
    }
  ) }) });
};
const BanksListView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      BanksListController,
      {
        onSelect: (item) => navigate(RoutePaths().banks.update(item.routingNumber))
      }
    ),
    /* @__PURE__ */ jsx(Outlet, {})
  ] });
};
const BankForm = ({ bank = {}, isRoutingNumberLocked = false, onChange }) => {
  return /* @__PURE__ */ jsxs(Uniformly, { className: "flex w-full flex-col gap-4 pb-6", onChange, children: [
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "routingNumber",
        label: "Routing Number",
        placeholder: "Enter 9-digit routing number",
        value: bank.routingNumber,
        isDisabled: isRoutingNumberLocked,
        validation: (x) => x.string().matches(/^\d{9}$/, "Routing number must be 9 digits").required("Routing number is required"),
        isRequired: true,
        className: "w-full",
        variant: "faded"
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "bankName",
        label: "Bank Name",
        placeholder: "Enter bank name",
        value: bank.bankName,
        validation: (x) => x.string().max(36).required("Bank name is required"),
        isRequired: true,
        className: "w-full",
        variant: "faded"
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "telegraphicName",
        label: "Telegraphic Name",
        placeholder: "Enter telegraphic name",
        value: bank.telegraphicName,
        validation: (x) => x.string().max(18),
        className: "w-full",
        variant: "faded"
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "city",
        label: "City",
        placeholder: "Enter city",
        value: bank.city,
        validation: (x) => x.string().max(25),
        className: "w-full",
        variant: "faded"
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "state",
        label: "State",
        placeholder: "Enter 2-letter state code",
        value: bank.state,
        validation: (x) => x.string().max(2),
        className: "w-full",
        variant: "faded"
      }
    ),
    /* @__PURE__ */ jsx(
      SwitchField,
      {
        className: "w-full",
        size: "sm",
        name: "pendingApproval",
        value: bank.pendingApproval || false,
        children: "Pending Approval"
      }
    )
  ] });
};
const BankCreateController = reactive.component(
  ({ services, monitors, onSuccess, onClose }) => {
    const [form, setForm] = useState({});
    useSuccessReactor(services.banks.createBank, (payload) => {
      onSuccess && onSuccess(payload);
    });
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(BankForm, { onChange: setForm }),
      /* @__PURE__ */ jsx(
        ResourceActions,
        {
          onCancel: onClose,
          onSave: () => services.banks.createBank(form.data),
          isSaveEnabled: form.isValid,
          isLoading: monitors.createBank.executing
        }
      )
    ] });
  },
  {
    name: "BankCreateController",
    monitors: (services) => [services.banks.createBank]
  }
);
BankCreateController.Modal = withModalController(({ onSuccess, onClose, ...props }) => /* @__PURE__ */ jsx(BankCreateController, { onSuccess, onClose, ...props }));
const BankCreateView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(
    BankCreateController.Modal,
    {
      title: "New Bank",
      isOpen: true,
      isDismissable: false,
      onSuccess: () => navigate(RoutePaths().banks.list()),
      onClose: () => navigate(RoutePaths().banks.list())
    }
  );
};
const BankUpdateController = reactive.component(
  ({ routingNumber, services, monitors, data, onSuccess, onClose }) => {
    const [form, setForm] = useState({});
    const { bank } = data;
    const { data: updatedBank } = form;
    useSuccessReactor(services.banks.updateBank, (payload) => {
      onSuccess && onSuccess(payload);
    });
    useSuccessReactor(services.banks.deleteBank, () => {
      onClose && onClose();
    });
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(BankForm, { bank, isRoutingNumberLocked: true, onChange: setForm }),
      /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-end gap-x-2", children: /* @__PURE__ */ jsx(
        ResourceActions,
        {
          onDelete: () => services.banks.deleteBank(routingNumber),
          onSave: () => services.banks.updateBank(routingNumber, { ...updatedBank, routingNumber }),
          onCancel: onClose,
          isSaveEnabled: form.isValid,
          isLoading: monitors.deleteBank.executing || monitors.updateBank.executing
        }
      ) })
    ] });
  },
  {
    name: "BankUpdateController",
    sync: (services, { routingNumber }) => [
      {
        execute: () => services.banks.getBank(routingNumber),
        deps: [routingNumber]
      }
    ],
    queries: ({ routingNumber }) => [
      {
        name: "bank",
        execute: (db) => db.banks.get(routingNumber),
        defaultValue: {},
        deps: [routingNumber]
      }
    ],
    monitors: (services) => [
      services.banks.updateBank,
      services.banks.deleteBank,
      services.banks.getBank
    ]
  }
);
BankUpdateController.Modal = withModalController(({ onSuccess, onClose, ...props }) => /* @__PURE__ */ jsx(BankUpdateController, { onSuccess, onClose, ...props }));
const BankUpdateView = ({ routingNumber }) => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(
    BankUpdateController.Modal,
    {
      title: "Update Bank",
      isOpen: true,
      isDismissable: false,
      routingNumber,
      onClose: () => navigate(RoutePaths().banks.list()),
      onSuccess: () => navigate(RoutePaths().banks.list())
    }
  );
};
const BanksRouter = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsxs(Route, { path: "/", element: /* @__PURE__ */ jsx(BanksListView, {}), children: [
  /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(BankCreateView, {}) }),
  /* @__PURE__ */ jsx(Route, { path: ":routingNumber/update", element: /* @__PURE__ */ jsx(WithParams, { element: BankUpdateView }) })
] }) });
const Router = () => /* @__PURE__ */ jsx(BaseLayout, { children: /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(
    Route,
    {
      index: true,
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "transactions", operation: "view", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(Navigate, { to: "view", replace: true }) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "batches/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "batches", operation: "view", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(BatchesRouter, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "profiles/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "profiles", operation: "view", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(ProfilesRouter, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "merchants/*",
      element: /* @__PURE__ */ jsx(
        RequirePolicy,
        {
          resource: "merchants",
          operation: ["create", "update", "delete"],
          placeholder: /* @__PURE__ */ jsx(Forbidden, {}),
          children: /* @__PURE__ */ jsx(MerchantsRouter, {})
        }
      )
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "payments/*",
      element: /* @__PURE__ */ jsx(
        RequirePolicy,
        {
          resource: "paymentImport",
          operation: ["create", "view"],
          placeholder: /* @__PURE__ */ jsx(Forbidden, {}),
          children: /* @__PURE__ */ jsx(PaymentsRouter, {})
        }
      )
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "banks/*",
      element: /* @__PURE__ */ jsx(
        RequirePolicy,
        {
          resource: "banks",
          operation: ["create", "update", "delete"],
          placeholder: /* @__PURE__ */ jsx(Forbidden, {}),
          children: /* @__PURE__ */ jsx(BanksRouter, {})
        }
      )
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "response-codes/*",
      element: /* @__PURE__ */ jsx(
        RequirePolicy,
        {
          resource: "responsecodes",
          operation: ["create", "update", "delete"],
          placeholder: /* @__PURE__ */ jsx(Forbidden, {}),
          children: /* @__PURE__ */ jsx(ResponseCodesRouter, {})
        }
      )
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "transactions", operation: "view", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(TransactionsRouter, {}) })
    }
  )
] }) });
function getTransactionsNavigation(runtime) {
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  return {
    isVisible: user.hasApiAccess(apiKey),
    items: [
      {
        title: "Transactions",
        route: "/view",
        icon: "ri-exchange-line",
        isVisible: user.hasPolicy(apiKey, "transactions", "view")
      },
      {
        title: "Batches",
        route: "/batches",
        icon: "ri-stack-line",
        isVisible: user.hasPolicy(apiKey, "batches", "view")
      },
      {
        title: "Payment Profiles",
        route: "/profiles",
        icon: "ri-wallet-line",
        isVisible: user.hasPolicy(apiKey, "profiles", "view")
      },
      {
        title: "Payments",
        isVisible: user.hasPolicy(apiKey, ["paymentImport"], "view", null, "any"),
        items: [
          {
            title: "Imports",
            route: "/payments/imports",
            icon: "ri-wallet-line",
            isVisible: user.hasPolicy(apiKey, "paymentImport", "view")
          }
        ]
      },
      {
        title: "Configuration",
        isVisible: user.hasPolicy(
          apiKey,
          ["merchants", "responsecodes", "banks"],
          "view",
          null,
          "any"
        ),
        items: [
          {
            title: "Banks",
            route: "/banks",
            icon: "ri-bank-line",
            isVisible: user.hasPolicy(apiKey, "banks", "view")
          },
          {
            title: "Merchants",
            route: "/merchants",
            icon: "ri-store-line",
            isVisible: user.hasPolicy(apiKey, "merchants", "view")
          },
          {
            title: "Response Codes",
            route: "/response-codes",
            icon: "ri-code-line",
            isVisible: user.hasPolicy(apiKey, "responsecodes", "view")
          }
        ]
      }
    ]
  };
}
function getTransactionsResources(runtime) {
  const items = [];
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  if (user.hasPolicy(apiKey, "batches", "create")) {
    items.push({
      key: "batch",
      label: "New Batch",
      route: "/batches",
      description: "Create transaction batch"
    });
  }
  if (user.hasPolicy(apiKey, "banks", "create")) {
    items.push({
      key: "bank",
      label: "New Bank",
      route: "/banks",
      description: "Create new bank"
    });
  }
  if (user.hasPolicy(apiKey, "merchants", "create")) {
    items.push({
      key: "merchant",
      label: "New Merchant",
      route: "/merchants",
      description: "Create new merchant"
    });
  }
  if (user.hasPolicy(apiKey, "profiles", "create")) {
    items.push({
      key: "profile",
      label: "New Profile",
      route: "/profiles",
      description: "Create new payment profile"
    });
  }
  if (user.hasPolicy(apiKey, "responsecodes", "create")) {
    items.push({
      key: "response-code",
      label: "New Response Code",
      route: "/response-codes",
      description: "Create new response code"
    });
  }
  if (user.hasPolicy(apiKey, "paymentImport", "create")) {
    items.push({
      key: "payment-import",
      label: "New Payment Import",
      route: "/payments/imports",
      description: "Create new payment import"
    });
  }
  return {
    items,
    handlers: ((navigate) => ({
      "bank": () => navigate(RoutePaths().banks.create()),
      "batch": () => navigate(RoutePaths().batches.create()),
      "merchant": () => navigate(RoutePaths().merchants.create()),
      "response-code": () => navigate(RoutePaths().responseCodes.create()),
      "profile": () => navigate(RoutePaths().profiles.create()),
      "payment-import": () => navigate(RoutePaths().payments.imports.create())
    }))
  };
}
const Embed = createEmbed({ runtime: () => appRuntime, render: render$2 });
const noop = () => {
};
const TransactionDetail$1 = ({
  transactionId,
  onSelectProfile = noop,
  onSelectBatch = noop
}) => {
  const [currentId, setCurrentId] = useState(transactionId);
  useEffect(() => setCurrentId(transactionId), [transactionId]);
  if (!currentId) return null;
  return /* @__PURE__ */ jsx(RequirePolicy, { resource: "Transactions", operation: "view", children: /* @__PURE__ */ jsx(
    TransactionDetailsController,
    {
      transactionId: currentId,
      onSelectTransaction: setCurrentId,
      onSelectProfile,
      onSelectBatch
    }
  ) });
};
const TRANSACTION_DETAIL = "transactions.transaction-detail";
const TransactionDetail = (props) => createElement(
  Embed,
  null,
  // The component is JS, so its props are inferred rather than declared. The interface above is
  // the contract; this cast is what ties the two together.
  createElement(TransactionDetail$1, props)
);
let appRuntime = null;
const initTransactionsRuntime = (hostRuntime) => {
  appRuntime = hostRuntime.registerApp("transactions", {
    description: {
      title: "Transactions",
      iconClass: "ri-exchange-dollar-line",
      defaultRoute: "/view",
      isVisible: (runtime) => runtime.user.hasApiAccess(runtime.config?.api?.apiKey ?? "")
    },
    routes: Router,
    navigation: getTransactionsNavigation,
    resources: getTransactionsResources,
    config: () => TransactionsConfig.current
  });
  hostRuntime.components.register(TRANSACTION_DETAIL, TransactionDetail);
};
export {
  initTransactionsRuntime
};
//# sourceMappingURL=index.js.map
